<template>
  <!-- Projects Dashboard -->
  <v-col>
    <h1 class="visually-hidden">
      Projects Dashboard
    </h1>

    <!-- Compact Header Section with Inline Tabs -->
    <div class="projects-view-header-compact mb-3">
      <v-toolbar
        flat
        color="white"
        class="header-toolbar px-0 py-0"
        height="56"
      >
        <!-- Inline Tabs -->
        <v-tabs
          v-model="activeTab"
          aria-label="Project status tabs"
          color="primary"
          slider-color="primary"
          show-arrows
          class="projects-view-tabs-inline flex-grow-1"
          height="56"
        >
          <v-tab
            v-for="tab in tabs"
            :key="tab.id"
            class="tab-compact"
          >
            <font-awesome-icon
              :icon="tab.icon"
              class="mr-2"
              size="sm"
              :class="tab.chipColor ? `${tab.chipColor}--text` : ''"
            />
            {{ tab.label }}
            <v-chip
              v-if="tab.count !== undefined"
              x-small
              outlined
              :color="tab.chipColor || 'default'"
              class="ml-2 tab-count-chip"
            >
              {{ tab.count }}
            </v-chip>
          </v-tab>
        </v-tabs>

        <v-spacer />
      </v-toolbar>

      <v-divider />

      <!-- Compact Controls Bar -->
      <div class="controls-bar">
        <v-row class="align-center ma-0 pa-3 pl-2">
          <!-- Search Field -->
          <v-col
            cols="12"
            sm="6"
            md="3"
            lg="2"
            class="py-1"
          >
            <search-field
              v-model="searchTerm"
              placeholder="Search..."
            />
          </v-col>

          <!-- Data Tools (Filter, Group, Sort, Columns, Jump) -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <tools-toolbar
              :current-required-columns="requiredColumns"
              :report-key="reportKey"
              :table-columns="tableColumns"
              @on-grouping-change="onGroupingChange"
              @on-sorting-change="onSortingChange"
              @on-reset-filters="onResetFilters"
            >
              <template v-slot:filter-controls>
                <filter-panel
                  v-model="localFilterData"
                  :fields="projectFilterFields"
                  view-name="projects"
                  title="Filter Projects"
                  :show-saved-filters="false"
                />
              </template>
            </tools-toolbar>
          </v-col>

          <!-- Parent/Child Quick Filters - hidden per request; parentChildFilter stays
               defaulted to 'all' so filtering logic is inert rather than removed. -->
          <v-col
            v-if="false"
            cols="12"
            sm="auto"
            class="py-1"
          >
            <v-btn-toggle
              v-model="parentChildFilter"
              mandatory
              class="parent-child-toggle"
            >
              <v-btn
                v-for="option in parentChildFilterOptions"
                :key="option.value"
                :value="option.value"
                outlined
                class="text-none elevation-0"
                small
              >
                <span>{{ option.label }}</span>
                <v-chip
                  x-small
                  outlined
                  class="ml-1"
                >
                  {{ option.count }}
                </v-chip>
              </v-btn>
            </v-btn-toggle>
          </v-col>

          <v-spacer class="d-none d-sm-flex" />

          <!-- View Mode Dropdown -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <v-menu
              offset-y
              nudge-bottom="6"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  outlined
                  class="text-none elevation-0"
                  v-bind="attrs"
                  v-on="on"
                >
                  <font-awesome-icon
                    :icon="viewMode === 'cards' ? ['fas', 'grip'] : ['fas', 'table']"
                    size="sm"
                    :class="{ 'mr-2': $vuetify.breakpoint.lgAndUp }"
                  />
                  <span class="d-none d-lg-inline">{{ viewMode === 'cards' ? 'Cards' : 'Table' }}</span>
                  <font-awesome-icon
                    :icon="['fas', 'chevron-down']"
                    size="xs"
                    class="ml-2 d-none d-lg-inline"
                  />
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item
                  :class="{ 'v-list-item--active primary--text': viewMode === 'cards' }"
                  @click="viewMode = 'cards'"
                >
                  <v-list-item-icon class="mr-3">
                    <font-awesome-icon :icon="['fas', 'grip']" />
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Card View</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="viewMode === 'cards'">
                    <font-awesome-icon
                      :icon="['fas', 'check']"
                      class="primary--text"
                    />
                  </v-list-item-action>
                </v-list-item>
                <v-list-item
                  :class="{ 'v-list-item--active primary--text': viewMode === 'table' }"
                  @click="viewMode = 'table'"
                >
                  <v-list-item-icon class="mr-3">
                    <font-awesome-icon :icon="['fas', 'table']" />
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Table View</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action v-if="viewMode === 'table'">
                    <font-awesome-icon
                      :icon="['fas', 'check']"
                      class="primary--text"
                    />
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-col>

          <!-- More Actions (Sort, Columns, Refresh, Export) -->
          <v-col
            cols="auto"
            class="py-1"
          >
            <v-menu
              offset-y
              nudge-bottom="6"
              :close-on-content-click="false"
              min-width="420"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  outlined
                  class="text-none elevation-0"
                  v-bind="attrs"
                  v-on="on"
                >
                  <font-awesome-icon
                    :icon="['fas', 'ellipsis-h']"
                    size="sm"
                    :class="{ 'mr-1': $vuetify.breakpoint.lgAndUp }"
                  />
                  <span class="d-none d-lg-inline">More</span>
                </v-btn>
              </template>

              <v-card class="pa-3">
                <div class="d-flex align-center mb-3">
                  <font-awesome-icon
                    :icon="['fas', 'sliders']"
                    class="mr-2"
                    size="sm"
                  />
                  <span class="subtitle-2 font-weight-medium">More Actions</span>
                </div>

                <div
                  class="d-flex align-center flex-wrap"
                  style="gap: 8px;"
                >
                  <v-btn
                    data-elementName="RefreshProjects"
                    outlined
                    class="text-none elevation-0"
                    :loading="isRefreshing"
                    :disabled="isRefreshing"
                    @click="refreshProjects"
                  >
                    <font-awesome-icon
                      :icon="['fas', 'arrows-rotate']"
                      size="sm"
                      :class="{ 'mr-1': $vuetify.breakpoint.lgAndUp }"
                    />
                    <span class="d-none d-lg-inline">Refresh</span>
                  </v-btn>

                  <v-menu
                    offset-y
                    nudge-bottom="6"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        outlined
                        class="text-none elevation-0"
                        :disabled="!showExportBar"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'file-export']"
                          size="sm"
                          :class="{ 'mr-1': $vuetify.breakpoint.lgAndUp }"
                        />
                        <span class="d-none d-lg-inline">Export</span>
                      </v-btn>
                    </template>

                    <v-list dense>
                      <v-list-item
                        v-for="item in exportOptions"
                        :key="item.key"
                        :disabled="!item.active"
                        @click="item.callback"
                      >
                        <v-list-item-title v-if="item.key !== 'xlsx'">
                          {{ item.text }}
                        </v-list-item-title>
                        <v-list-item-title v-else>
                          <export-xlsx
                            :item="item"
                            :show-export-bar="showExportBar"
                            :columns="exportColumns"
                            :groups="exportGroups"
                            :export-report-name="exportReportName"
                          />
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </v-card>
            </v-menu>
          </v-col>

          <!-- Add Engagement Button (Admin Only) -->
          <v-col
            v-if="isUserInternalAdmin"
            cols="auto"
            class="py-1"
          >
            <v-btn
              outlined
              class="text-none elevation-0"
              @click="showFormProject"
            >
              <font-awesome-icon
                :icon="['fas', 'plus']"
                size="sm"
                :class="{ 'mr-1': $vuetify.breakpoint.lgAndUp }"
              />
              <span class="d-none d-lg-inline">Add Engagement</span>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- Jump to Group (contextual row) -->
    <jump-to-group
      :groups="exportGroups"
      class="ma-0 mb-4 ml-3"
      @scroll-to-group="scrollToGroup"
    />

    <!-- Projects -->
    <v-row v-if="haveProjects">
      <v-col>
        <!-- Card View -->
        <v-row v-if="viewMode === 'cards'">
          <v-col>
            <!-- Regular View: All Projects -->
            <div v-if="!isGroupingEnabled">
              <!-- List of All Projects -->
              <v-row>
                <v-col>
                  <div class="project-card-group-header">
                    <button
                      type="button"
                      class="project-card-group-toggle"
                      :aria-expanded="showAllProjects ? 'true' : 'false'"
                      @click.stop="toggleAllProjects"
                    >
                      <span class="project-card-group-toggle-icon">
                        <font-awesome-icon
                          :icon="['fas', showAllProjects ? 'chevron-down' : 'chevron-right']"
                          size="xs"
                        />
                      </span>

                      <span class="project-card-group-copy">
                        <span class="project-card-group-label">All</span>
                        <span class="project-card-group-meta">{{ allProjects.length }} project{{ allProjects.length === 1 ? '' : 's' }}</span>
                      </span>
                    </button>
                  </div>

                  <!-- Cards List: All Projects -->
                  <v-row v-if="showAllProjects">
                    <v-container fluid>
                      <v-row>
                        <v-col
                          v-for="(project, index) in allProjects"
                          :key="`all-row-${index}-${project.projectId || project.docId}`"
                          cols="12"
                          sm="6"
                          md="4"
                          lg="3"
                          xl="3"
                          class="d-flex"
                        >
                          <ProjectCard
                            :key="`card-${index}-${project.projectId || project.docId}`"
                            :actions="actions"
                            :child-count="childCountByParent[project.projectCode] || 0"
                            :client-name="clientLookup[project.billingCode]"
                            :loading-client="loadingClients"
                            :organization-href="project.billingCode ? organizationPath.profile(project.billingCode) : null"
                            :parent-name="project.parentProjectId ? parentNameByCode[project.parentProjectId] : null"
                            :project="project"
                            :project-status="projectStatusMap[project.projectStatus] || project.projectStatus"
                            :project-type="getProjectTypeDisplay(project)"
                            :visible-columns="storeVisibleColumns"
                            class="flex-fill"
                            @assign-client="onAssignClient"
                            @go-to-project="goToProject"
                            @show-time-entries="showTimeEntries(project)"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-row>
                </v-col>
              </v-row>
            </div>

            <!-- Grouped View -->
            <div v-else>
              <v-row
                v-for="(groupOfProjects, groupName) in groupedProjects"
                :id="`group-${groupName}`"
                :key="groupName"
                class="project-card-group-row"
              >
                <v-col>
                  <!-- Group Header -->
                  <div class="project-card-group-header">
                    <button
                      type="button"
                      class="project-card-group-toggle"
                      :aria-expanded="expandedGroups[groupName] ? 'true' : 'false'"
                      @click.stop="toggleGroup(groupName)"
                    >
                      <span class="project-card-group-toggle-icon">
                        <font-awesome-icon
                          :icon="['fas', expandedGroups[groupName] ? 'chevron-down' : 'chevron-right']"
                          size="xs"
                        />
                      </span>

                      <span class="project-card-group-copy">
                        <span class="project-card-group-label">{{ getGroupLabel(groupName) }}</span>
                        <span class="project-card-group-meta">{{ groupOfProjects.length }} project{{ groupOfProjects.length === 1 ? '' : 's' }}</span>
                      </span>
                    </button>
                  </div>

                  <!-- Group Projects -->
                  <v-row v-if="expandedGroups[groupName]">
                    <v-container fluid>
                      <v-row>
                        <v-col
                          v-for="(project, index) in groupOfProjects"
                          :key="`group-${groupName}-${index}-${project.projectId || project.docId}`"
                          cols="12"
                          sm="6"
                          md="4"
                          lg="3"
                          xl="2"
                          class="d-flex"
                        >
                          <ProjectCard
                            :key="`grouped-card-${index}-${project.projectId || project.docId}`"
                            :actions="actions"
                            :child-count="childCountByParent[project.projectCode] || 0"
                            :client-name="clientLookup[project.billingCode]"
                            :loading-client="loadingClients"
                            :organization-href="project.billingCode ? organizationPath.profile(project.billingCode) : null"
                            :parent-name="project.parentProjectId ? parentNameByCode[project.parentProjectId] : null"
                            :project="project"
                            :project-status="projectStatusMap[project.projectStatus] || project.projectStatus"
                            :project-type="getProjectTypeDisplay(project)"
                            :visible-columns="storeVisibleColumns"
                            class="flex-fill"
                            @assign-client="onAssignClient"
                            @go-to-project="goToProject"
                            @show-time-entries="showTimeEntries(project)"
                          />
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-row>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>

        <!-- Table View -->
        <v-row v-else-if="viewMode === 'table'">
          <v-col>
            <!-- Projects Table -->
            <v-row>
              <v-col>
                <data-table
                  v-model="selectedProjects"
                  :actions="actions"
                  :data-groups="currentDataGroups"
                  :hide-toolbar-buttons="true"
                  show-select
                  item-key="__rowKey"
                  report-key="projects"
                  @click:row="goToProject"
                >
                  <!-- Bulk-edit actions: live in the DataTable's bottom bar -->
                  <template
                    v-if="selectedProjects.length > 0"
                    v-slot:bottom-actions
                  >
                    <div class="d-flex align-center flex-wrap">
                      <span class="caption font-weight-medium mr-3">
                        {{ selectedProjects.length }} selected
                      </span>
                      <v-btn
                        color="primary"
                        outlined
                        small
                        class="text-none"
                        @click="openBulkEditDialog"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'pen-to-square']"
                          size="sm"
                          class="mr-1"
                        />
                        Bulk Edit
                      </v-btn>
                      <v-btn
                        color="warning"
                        outlined
                        small
                        class="ml-2 text-none"
                        @click="bulkArchiveProjects"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'box-archive']"
                          size="sm"
                          class="mr-1"
                        />
                        Archive
                      </v-btn>
                      <v-btn
                        color="error"
                        outlined
                        small
                        class="ml-2 text-none"
                        @click="bulkDeleteProjects"
                      >
                        <font-awesome-icon
                          :icon="['fas', 'trash']"
                          size="sm"
                          class="mr-1"
                        />
                        Delete
                      </v-btn>
                      <v-btn
                        icon
                        small
                        class="ml-1"
                        title="Clear selection"
                        @click="clearSelection"
                      >
                        <font-awesome-icon :icon="['fas', 'xmark']" />
                      </v-btn>
                    </div>
                  </template>
                  <!-- Project Name Column -->
                  <template v-slot:item.projectName="{ item }">
                    <div class="font-weight-medium">
                      <span
                        class="cursor-pointer primary--text"
                        @click.stop="goToProject(item)"
                      >
                        {{ item.projectName }}
                      </span>
                    </div>
                  </template>

                  <!-- Status Column -->
                  <template v-slot:item.projectStatus="{ item }">
                    <v-chip
                      :color="item.projectStatusColorBackground"
                      :text-color="item.projectStatusColorText"
                      small
                    >
                      {{ projectStatusMap[item.projectStatus] }}
                    </v-chip>
                  </template>

                  <template v-slot:item.projectType="{ item }">
                    <div class="d-flex flex-wrap">
                      <v-chip
                        v-for="type in getProjectTypeLabels(item)"
                        :key="`${item.projectId || item.projectCode}-${type}`"
                        small
                        class="mr-1"
                      >
                        {{ type }}
                      </v-chip>
                    </div>
                  </template>

                  <template v-slot:item.docs="{ item }">
                    <div
                      v-if="normalizedProjectDocs(item).length"
                      class="d-flex flex-column align-start"
                    >
                      <a
                        v-for="(doc, index) in normalizedProjectDocs(item).slice(0, 2)"
                        :key="`${item.projectId || item.projectCode}-doc-${index}`"
                        :href="doc.url"
                        class="caption font-weight-medium primary--text text-decoration-none"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop
                      >
                        {{ doc.title || doc.url }}
                      </a>
                      <span
                        v-if="normalizedProjectDocs(item).length > 2"
                        class="caption grey--text"
                      >
                        +{{ normalizedProjectDocs(item).length - 2 }} more
                      </span>
                    </div>
                    <span
                      v-else
                      class="caption grey--text"
                    >No docs</span>
                  </template>

                  <!-- Progress Column -->
                  <template v-slot:item.progress="{ item }">
                    <div>
                      <v-progress-linear
                        :value="item.projectMinutesConsumedPercentage"
                        :color="item.projectStatusColorBackground"
                        height="8"
                        class="mb-1"
                      />
                      <div class="caption text-center">
                        {{ item.projectMinutesConsumedPercentFormatted }}%
                      </div>
                    </div>
                  </template>

                  <!-- Time Consumed Column -->
                  <template v-slot:item.timeConsumed="{ item }">
                    <div class="text-no-wrap">
                      <div>{{ item.totalBillableTimeFormatted }} / {{ item.projectHoursFormatted }} hrs</div>
                      <div
                        class="caption cursor-pointer primary--text"
                        @click.stop="showTimeEntries(item)"
                      >
                        View Details
                      </div>
                    </div>
                  </template>

                  <!-- Days Left Column -->
                  <template v-slot:item.daysLeft="{ item }">
                    <div class="text-center">
                      <div class="font-weight-medium">
                        {{ item.projectDaysLeft }}
                      </div>
                      <div class="caption">
                        days
                      </div>
                    </div>
                  </template>

                  <!-- Actions Column -->
                  <template v-slot:item.actions="{ item }">
                    <v-menu offset-y>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          @click.stop
                        >
                          <font-awesome-icon
                            :icon="['fas', 'ellipsis-vertical']"
                            size="sm"
                          />
                        </v-btn>
                      </template>

                      <v-list dense>
                        <v-list-item
                          v-for="action in actions"
                          :key="action.name"
                          link
                          @click="action.event(item)"
                        >
                          <v-list-item-icon class="mr-3">
                            <font-awesome-icon
                              :icon="action.icon"
                              size="sm"
                            />
                          </v-list-item-icon>
                          <v-list-item-title>{{ action.name }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </template>
                </data-table>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!-- No Projects Found -->
    <v-row v-else>
      <v-col>
        <v-card class="px-3 pt-3 pb-6 elevation-0">
          <v-card-text>
            <v-row>
              <v-col>
                <v-row class="subtitle-2">
                  <v-col class="midgray--text">
                    No projects found. Click "Add Engagement" to create your first project.
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Show Time Entry View -->
    <v-dialog
      v-model="showTimeEntryView"
      max-width="1200px"
    >
      <v-card>
        <v-card-title class="headline">
          <v-row class="ma-0 align-center">
            <v-col class="col-auto">
              <span>Time Entries</span>

              <div class="body-2">
                <span class="font-weight-medium gray--text">{{ currentProject.projectName }}</span>
              </div>
            </v-col>

            <v-col class="text-right">
              <!-- Download Button -->
              <v-btn
                class="px-3 mx-3"
                color="primary"
                :ripple="false"
                @click.stop="exportTimeEntriesAsCsv(currentProject)"
              >
                Export
              </v-btn>

              <v-btn
                :ripple="false"
                icon
                @click.stop="closeTimeEntryView"
              >
                <v-icon color="midgray">
                  close
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider />

        <v-card-text class="font-weight-medium">
          <v-row>
            <!-- Table of Time Entries -->
            <v-col>
              <v-row>
                <v-col>
                  <v-data-table
                    :headers="timeEntryHeaders"
                    :items="currentTimeEntries"
                    :loading="isLoadingTimeEntries"
                  >
                    <template v-slot:body.append>
                      <!-- Add row for totals -->
                      <tr>
                        <td>
                          <span class="font-weight-bold">Summaries</span>
                          <br>
                        </td>

                        <td colspan="3" />

                        <td>
                          <span>{{ totalMinutesFormatted }} min</span>
                          <br>
                          <span class="vuetifygray--text">{{ totalHoursFormatted }} hrs</span>
                        </td>

                        <td>
                          <span>{{ totalBillableMinutesFormatted }} min</span>
                          <br>
                          <span class="vuetifygray--text">{{ totalBillableHoursFormatted }} hrs</span>
                        </td>
                      </tr>
                    </template>

                    <template v-slot:no-data>
                      No time entries found for this project.
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Bulk Edit Dialog -->
    <v-dialog
      v-model="bulkEditDialog"
      max-width="600px"
    >
      <v-card>
        <v-card-title class="headline">
          Bulk Edit Projects
        </v-card-title>

        <v-card-text>
          <v-form ref="bulkEditForm">
            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="bulkEditData.projectStatus"
                  :items="projectStatusOptions"
                  label="Project Status"
                  outlined
                  dense
                >
                  <template v-slot:append-inner>
                    <font-awesome-icon
                      v-if="bulkEditData.projectStatus"
                      :icon="['fas', 'xmark']"
                      size="sm"
                      class="cursor-pointer grey--text mt-1"
                      @click="bulkEditData.projectStatus = null"
                    />
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-select
                  v-model="bulkEditData.projectType"
                  :items="projectTypeOptions"
                  label="Project Type"
                  outlined
                  dense
                >
                  <template v-slot:append-inner>
                    <font-awesome-icon
                      v-if="bulkEditData.projectType"
                      :icon="['fas', 'xmark']"
                      size="sm"
                      class="cursor-pointer grey--text mt-1"
                      @click="bulkEditData.projectType = null"
                    />
                  </template>
                </v-select>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="bulkEditData.startDate"
                      label="Start Date"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    >
                      <template v-slot:prepend>
                        <font-awesome-icon
                          :icon="['fas', 'calendar']"
                          size="sm"
                          class="mt-1"
                        />
                      </template>
                      <template v-slot:append-inner>
                        <font-awesome-icon
                          v-if="bulkEditData.startDate"
                          :icon="['fas', 'xmark']"
                          size="sm"
                          class="cursor-pointer grey--text mt-1"
                          @click="bulkEditData.startDate = null"
                        />
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="bulkEditData.startDate"
                    @input="startDateMenu = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="6">
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="bulkEditData.endDate"
                      label="End Date"
                      readonly
                      outlined
                      dense
                      v-bind="attrs"
                      v-on="on"
                    >
                      <template v-slot:prepend>
                        <font-awesome-icon
                          :icon="['fas', 'calendar']"
                          size="sm"
                          class="mt-1"
                        />
                      </template>
                      <template v-slot:append-inner>
                        <font-awesome-icon
                          v-if="bulkEditData.endDate"
                          :icon="['fas', 'xmark']"
                          size="sm"
                          class="cursor-pointer grey--text mt-1"
                          @click="bulkEditData.endDate = null"
                        />
                      </template>
                    </v-text-field>
                  </template>
                  <v-date-picker
                    v-model="bulkEditData.endDate"
                    @input="endDateMenu = false"
                  />
                </v-menu>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-alert
                  type="info"
                  dense
                  text
                >
                  Only filled fields will be applied to the selected {{ selectedProjects.length }} project(s).
                  Empty fields will be ignored.
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />

          <v-btn
            color="grey darken-1"
            text
            @click="closeBulkEditDialog"
          >
            Cancel
          </v-btn>

          <v-btn
            color="primary"
            text
            @click="applyBulkEdit"
          >
            Apply Changes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Project Code Dialog -->
    <v-dialog
      v-model="editCodeDialog"
      max-width="480"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          <font-awesome-icon
            :icon="['fas', 'tag']"
            class="mr-2 primary--text"
            size="sm"
          />
          Edit Project Code
        </v-card-title>
        <v-divider />

        <!-- Step 1: Input -->
        <template v-if="!editCodeReviewStep">
          <v-card-text class="pt-4">
            <div class="mb-3 body-2">
              Current code:
              <strong>{{ editCodeProject && editCodeProject.projectCode }}</strong>
            </div>
            <v-text-field
              v-model="editCodeNewValue"
              label="New Project Code"
              outlined
              dense
              autofocus
              :error-messages="editCodeError ? [editCodeError] : []"
              hint="Letters, numbers, hyphens, and underscores only. Must be unique."
              persistent-hint
              @keydown.enter="reviewEditCode"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              class="text-none"
              @click="closeEditCodeDialog"
            >
              Cancel
            </v-btn>
            <v-btn
              outlined
              color="primary"
              class="text-none"
              :disabled="!editCodeNewValue || editCodeNewValue === (editCodeProject && editCodeProject.projectCode)"
              @click="reviewEditCode"
            >
              Review Change
            </v-btn>
          </v-card-actions>
        </template>

        <!-- Step 2: Review & Confirm -->
        <template v-else>
          <v-card-text class="pt-4">
            <v-alert
              type="warning"
              outlined
              dense
              class="mb-3"
            >
              This action will rename the project code. Any existing references
              to the old code will be superseded and marked as deleted.
            </v-alert>
            <v-list dense>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>From</v-list-item-subtitle>
                  <v-list-item-title class="font-weight-medium">
                    {{ editCodeProject && editCodeProject.projectCode }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>To</v-list-item-subtitle>
                  <v-list-item-title class="font-weight-medium primary--text">
                    {{ editCodeNewValue.toUpperCase() }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>Project</v-list-item-subtitle>
                  <v-list-item-title>
                    {{ editCodeProject && editCodeProject.projectName }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
            <v-alert
              v-if="editCodeError"
              type="error"
              dense
              outlined
              class="mt-2"
            >
              {{ editCodeError }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              class="text-none"
              :disabled="editCodeSaving"
              @click="editCodeReviewStep = false"
            >
              Back
            </v-btn>
            <v-spacer />
            <v-btn
              text
              class="text-none"
              :disabled="editCodeSaving"
              @click="closeEditCodeDialog"
            >
              Cancel
            </v-btn>
            <v-btn
              outlined
              color="primary"
              class="text-none"
              :loading="editCodeSaving"
              @click="confirmEditCode"
            >
              Confirm Change
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <!-- Mobile FAB: Add Engagement (sm and down) -->
    <v-btn
      v-if="isUserInternalAdmin && $vuetify.breakpoint.smAndDown"
      fab
      fixed
      bottom
      right
      color="primary"
      class="mb-4 mr-2"
      @click="showFormProject"
    >
      <font-awesome-icon
        :icon="['fas', 'plus']"
        size="lg"
      />
    </v-btn>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import { onSnapshot } from 'firebase/firestore'

import dates from 'src/utils/dates'
import format from 'src/utils/format.js'
import { registerListener, unregisterListener } from '@/services/listenerRegistry'
import api from '@/store/api.js'
import { firecollection } from '@/store/firebase'
import columns from '@/internal/store/utils/columns.js'
import { makeProject } from '@/internal/store/utils/project.js'
import columnUtils from '@/tools/store/utils/columns.js'
import { organizationPath } from '@/utils/internalPaths'
import ProjectCard from './ProjectCard.vue'
import tabQuerySync from '@/mixins/tabQuerySync.js'

const ExportXlsx = () => import('@/tools/components/export/ExportXlsx')
const PROJECT_FIRESTORE_LISTENER_KEY = 'projects:firestore'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapToolsGetters } = createNamespacedHelpers('tools')

export default {
  name: 'ProjectDashboard',
  components: {
    DataTable: () => import('@/tools/components/table/DataTable'),
    ExportXlsx,
    ProjectCard,
    filterPanel: () => import('@/tools/components/filter/FilterPanel.vue'),
    ToolsToolbar: () => import('@/tools/components/filter/ToolsToolbar.vue'),
    JumpToGroup: () => import('@/tools/components/filter/JumpToGroup.vue'),
    SearchField: () => import('@/tools/components/filter/SearchField.vue'),
  },
  mixins: [tabQuerySync],
  data() {
    return {
      // Tab query sync configuration
      tabQueryConfig: {
        queryParam: 'tab',
        validValues: ['all', 'recently-updated', 'active', 'complete', 'on-hold', 'archived', 'deleted'],
        defaultValue: 'all',
        additionalValidParams: ['search', 'view', 'groupByProperty'],
      },

      // Tab configuration
      tabs: [
        {
          id: 'all',
          label: 'All Projects',
          icon: ['fas', 'folder-open'],
          chipColor: 'primary',
        },
        {
          id: 'recently-updated',
          label: 'Recently Updated',
          icon: ['fas', 'clock'],
          chipColor: 'info',
        },
        {
          id: 'active',
          label: 'Active',
          icon: ['fas', 'play-circle'],
          chipColor: 'default',
        },
        {
          id: 'complete',
          label: 'Complete',
          icon: ['fas', 'check-circle'],
          chipColor: 'default',
        },
        {
          id: 'on-hold',
          label: 'On Hold',
          icon: ['fas', 'pause-circle'],
          chipColor: 'default',
        },
        {
          id: 'archived',
          label: 'Archived',
          icon: ['fas', 'archive'],
          chipColor: 'error',
        },
        {
          id: 'deleted',
          label: 'Deleted',
          icon: ['fas', 'trash'],
          chipColor: 'error',
        },
      ],

      // Data Tools
      searchTerm: '',
      parentChildFilter: 'all',

      // Client lookup (billingCode → clientName)
      clientLookup: {},
      loadingClients: false,

      // Component-level filter state (for FilterPanel)
      localFilterData: {
        billingCode: null,
        projectStatus: null,
        projectType: null,
        projectPlatforms: null,
        hasDocs: null,
        hasTimeEntries: null,
      },

      // View Controls
      viewMode: 'cards',
      groupByProperties: [],
      visibleColumns: ['projectName', 'projectCode', 'projectType', 'projectStatus', 'progress', 'timeConsumed', 'startDateFormatted', 'endDateFormatted', 'daysLeft', 'actions'],
      selectedProjects: [],
      bulkEditDialog: false,
      bulkEditData: {
        projectStatus: '',
        projectType: '',
        startDate: null,
        endDate: null,
      },

      // Dialog Controls
      startDateMenu: false,
      endDateMenu: false,

      // Flags
      showAllProjects: true,
      testingTimeEntries: false,
      expandedGroups: {},

      // Time Entry View
      currentProject: {},
      currentTimeEntries: [],
      showTimeEntryView: false,
      sortDirectionForTimeEntries: 'DSC',
      totalMinutes: 0,
      totalMinutesFormatted: '0',
      totalHoursFormatted: '0.00',
      totalBillableMinutes: 0,
      totalBillableMinutesFormatted: '0',
      totalBillableHoursFormatted: '0.00',
      totalPersons: 0,

      isLoadingTimeEntries: false,
      projectsById: {},
      projectsWithTimeEntries: [],

      // Reactive incrementor to force view updates
      projectLoadingCounter: 0,

      // Flag to prevent circular dependency when loading time entries
      isLoadingProjectTimeEntries: false,

      // Refresh state
      isRefreshing: false,

      processedEditProjectQuery: null,

      // Real-time Firestore listener unsubscribe function
      firestoreListenerUnsubscribe: null,

      // Project Code Edit Dialog
      editCodeDialog: false,
      editCodeProject: null,
      editCodeNewValue: '',
      editCodeReviewStep: false,
      editCodeSaving: false,
      editCodeError: '',

      timeEntryHeaders: [
        { text: 'Date', value: 'timeEntryDateFormatted', sortable: true },
        { text: 'Person', value: 'personName', sortable: true },
        { text: 'Team', value: 'teamName', sortable: true },
        { text: 'Description', value: 'workNote', sortable: false },
        { text: 'Minutes', value: 'actionDuration', sortable: true },
        { text: 'Billable Minutes', value: 'billableActionDuration', sortable: true },
      ],

      timeSortByKeys: [
        {
          key: 'timeEntryDate',
          direction: 'DSC',
        },
        {
          key: 'personName',
          direction: 'ASC',
        },
      ],

    }
  },
  computed: {
    organizationPath() {
      return organizationPath
    },
    ...mapAuthGetters([
      'isUserInternalAdmin',
      'isInternalUser',
      'userEmail',
    ]),
    // Safely access isProjectModalVisible from internal module if registered
    isProjectModalVisible() {
      return this.$store.state.internal
        ? this.$store.state.internal.isProjectModalVisible
        : false
    },
    // Track whether a project was saved/deleted (vs cancelled)
    projectModified: {
      get() {
        return this.$store.state.internal
          ? this.$store.state.internal.projectModified
          : false
      },
      set(value) {
        this.$store.commit('internal/projectModified', value)
      },
    },
    ...mapToolsGetters([
      'currentGroupByProperties',
      'projects',
      'projectPlatforms',
      'projectTimeColumns',
      'projectStatuses',
      'projectTypes',
      'reportKey',
      'sortByKeys',
      'sortByDirections',
      'tableColumns',
    ]),

    /**
     * Active tab index - syncs with URL query parameter
     */
    activeTab: {
      get() {
        return this.activeTabIndex
      },
      set(index) {
        this.setActiveTab(index)
      },
    },

    $projects: {
      get() {
        // Force reactivity when projects finish loading (reference the counter)
        this.projectLoadingCounter // eslint-disable-line no-unused-expressions

        // Ensure projects is always an array before spreading
        const projectsArray = Array.isArray(this.projects) ? this.projects : []

        // Merge this.projects with the projects that have time entries calculated.
        const currentProjects = [...projectsArray].map((project) => {
          if (!project.projectId) {
            return project
          }

          const projectWithTimeEntries = this.projectsById[project.projectId]
          return projectWithTimeEntries || project
        })

        const getUndeletedProjects = (projects) => projects.filter((project) => !project.isDeleted && !project.isTest)

        return getUndeletedProjects(currentProjects)
      },
      set(value) {
        // Update projects in the store.
        this.$store.commit('tools/projects', value, { root: true })
      },
    },

    haveProjects() {
      return Array.isArray(this.$projects) && this.$projects.length > 0
    },

    // Computed property that ensures cards update when time entries load
    reactiveProjects() {
      // This forces recalculation when projects finish loading
      this.projectLoadingCounter // eslint-disable-line no-unused-expressions
      return this.$projects
    },

    /**
     * Deleted projects from the raw store (bypasses the $projects filter).
     */
    deletedProjects() {
      this.projectLoadingCounter // eslint-disable-line no-unused-expressions
      const raw = Array.isArray(this.projects) ? this.projects : []
      return raw.filter((p) => p.isDeleted && !p.isTest)
    },

    currentTab() {
      return this.tabs[this.activeTab] || this.tabs[0]
    },

    /**
     * Get projects for the current tab based on status
     */
    tabProjects() {
      const { id } = this.currentTab
      const allProjects = this.reactiveProjects || []
      const getTimestamp = (rawDate) => {
        if (!rawDate) return 0
        if (rawDate.toDate) return rawDate.toDate().getTime()
        return new Date(rawDate).getTime()
      }

      switch (id) {
        case 'recently-updated':
          // Show projects updated in the last 7 days
          return allProjects.filter((project) => {
            if (project.isDeleted || project.isArchived || project.isTest) return false
            const updatedTime = getTimestamp(project.updatedAt)
            const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000)
            return updatedTime > sevenDaysAgo
          }).sort((a, b) => {
            // Sort by updatedAt descending (most recent first)
            const timeA = getTimestamp(a.updatedAt)
            const timeB = getTimestamp(b.updatedAt)
            return timeB - timeA
          })

        case 'active':
          return allProjects.filter((project) => {
            const { isDeleted, isArchived, isTest, projectStatus } = project
            // Exclude archived and deleted projects from Active tab
            // Includes: in-progress, active, planning, scoping, launching
            return !isDeleted && !isArchived && !isTest && (
              projectStatus === 'in-progress'
              || projectStatus === 'active'
              || projectStatus === 'planning'
              || projectStatus === 'scoping'
              || projectStatus === 'launching'
            )
          })

        case 'complete':
          return allProjects.filter((project) => {
            const { isDeleted, isArchived, isTest, projectStatus } = project
            // Exclude archived and deleted projects from Complete tab
            return !isDeleted && !isArchived && !isTest && (
              projectStatus === 'complete'
              || projectStatus === 'done'
            )
          })

        case 'on-hold':
          return allProjects.filter((project) => {
            const { isDeleted, isArchived, isTest, projectStatus } = project
            // Exclude archived and deleted projects from On Hold tab
            return !isDeleted && !isArchived && !isTest && (
              projectStatus === 'on-hold'
              || projectStatus === 'paused'
            )
          })

        case 'archived':
          return allProjects.filter((project) => {
            const { isDeleted, isArchived, isTest } = project
            return !isDeleted && !isTest && isArchived
          })

        case 'deleted':
          return this.deletedProjects

        case 'all':
        default: {
          // Exclude archived, deleted, and test projects from the All tab.
          // Also hide unpublished engagements from users who are not the
          // creator, project owner, assigned team member, or an admin.
          const email = this.userEmail
          const isAdmin = this.isUserInternalAdmin
          return allProjects.filter((project) => {
            if (project.isArchived || project.isDeleted || project.isTest) return false
            if (project.isPublished !== false) return true
            if (isAdmin) return true
            return (
              project.createdBy === email
              || project.projectOwner === email
              || project.emailAccountManager === email
            )
          })
        }
      }
    },

    parentChildFilterOptions() {
      const projects = this.tabProjects || []
      const getChildCount = (project) => this.childCountByParent[project.projectCode] || 0

      const parentCount = projects.filter((project) => !project.parentProjectId && getChildCount(project) > 0).length
      const childCount = projects.filter((project) => !!project.parentProjectId).length
      const unlinkedCount = projects.filter((project) => !project.parentProjectId && getChildCount(project) === 0).length

      return [
        { value: 'all', label: 'All', count: projects.length },
        { value: 'parents', label: 'Parents', count: parentCount },
        { value: 'children', label: 'Children', count: childCount },
        { value: 'unlinked', label: 'Unlinked', count: unlinkedCount },
      ]
    },

    projectResults() {
      let filteredProjects = this.tabProjects || []

      if (this.parentChildFilter !== 'all') {
        filteredProjects = filteredProjects.filter((project) => this.matchesParentChildFilter(project, this.parentChildFilter))
      }

      // Apply filters first
      if (this.hasActiveFilters) {
        filteredProjects = filteredProjects.filter((project) => {
          // Filter by billing code (supports multi-select array)
          const billingCodeFilter = this.localFilterData.billingCode
          if (billingCodeFilter) {
            const billingCodes = Array.isArray(billingCodeFilter) ? billingCodeFilter : [billingCodeFilter]
            if (billingCodes.length > 0) {
              const projectCode = (project.billingCode || '').substring(0, 8)
              if (!billingCodes.some((code) => projectCode.startsWith(code))) {
                return false
              }
            }
          }

          // Filter by project status (supports multi-select array)
          const statusFilter = this.localFilterData.projectStatus
          if (statusFilter) {
            const statuses = Array.isArray(statusFilter) ? statusFilter : [statusFilter]
            if (statuses.length > 0) {
              // Handle __unknown__ status specially
              if (statuses.includes('__unknown__')) {
                const hasNoStatus = !project.projectStatus || project.projectStatus.trim() === ''
                if (!hasNoStatus && !statuses.includes(project.projectStatus)) {
                  return false
                }
              } else if (!statuses.includes(project.projectStatus)) {
                return false
              }
            }
          }

          // Filter by project type (supports multi-select array)
          const typeFilter = this.localFilterData.projectType
          if (typeFilter) {
            const types = Array.isArray(typeFilter) ? typeFilter : [typeFilter]
            if (types.length > 0 && !types.some((type) => this.projectMatchesType(project, type))) {
              return false
            }
          }

          // Filter by platform (supports multi-select array)
          const platformFilter = this.localFilterData.projectPlatforms
          if (platformFilter) {
            const platforms = Array.isArray(platformFilter) ? platformFilter : [platformFilter]
            if (platforms.length > 0) {
              const projectPlatforms = Array.isArray(project.projectPlatforms) ? project.projectPlatforms : []
              if (!platforms.some((p) => projectPlatforms.includes(p))) {
                return false
              }
            }
          }

          // Filter by has docs
          const hasDocsFilter = this.localFilterData.hasDocs
          if (hasDocsFilter !== null && hasDocsFilter !== undefined) {
            const projectHasDocs = this.normalizedProjectDocs(project).length > 0
            if (hasDocsFilter !== projectHasDocs) {
              return false
            }
          }

          // Filter by has time entries
          const hasTimeEntriesFilter = this.localFilterData.hasTimeEntries
          if (hasTimeEntriesFilter !== null && hasTimeEntriesFilter !== undefined) {
            const projectHasTimeEntries = (project.timeEntries && project.timeEntries.length > 0) || false
            if (hasTimeEntriesFilter !== projectHasTimeEntries) {
              return false
            }
          }

          return true
        })
      }

      // Apply search term filtering
      if (this.searchTerm.length > 0) {
        const searchTerm = this.searchTerm.toLowerCase()

        filteredProjects = filteredProjects.filter((project) => {
          const billingCode = (project.billingCode || '').toLowerCase()
          const projectName = (project.projectName || '').toLowerCase()
          const projectCode = (project.projectCode || '').toLowerCase()
          const projectType = this.getProjectTypeDisplay(project).toLowerCase()
          const projectDescription = (project.projectDescription || '').toLowerCase()
          const projectStatus = (project.projectStatus || '').toLowerCase()
          const projectId = String(project.projectId || project.project_id || '')
          const startDateFormatted = (project.startDateFormatted || '').toLowerCase()
          const endDateFormatted = (project.endDateFormatted || '').toLowerCase()
          const createdDateFormatted = (project.createdDateFormatted || '').toLowerCase()
          const updatedDateFormatted = (project.updatedDateFormatted || '').toLowerCase()
          const projectDocs = this.normalizedProjectDocs(project)
            .map((doc) => `${doc.title} ${doc.url}`.trim().toLowerCase())
            .join(' ')

          return (
            projectName.indexOf(searchTerm) > -1
            || billingCode.indexOf(searchTerm) > -1
            || projectCode.indexOf(searchTerm) > -1
            || projectType.indexOf(searchTerm) > -1
            || projectDescription.indexOf(searchTerm) > -1
            || projectStatus.indexOf(searchTerm) > -1
            || projectId.indexOf(searchTerm) > -1
            || startDateFormatted.indexOf(searchTerm) > -1
            || endDateFormatted.indexOf(searchTerm) > -1
            || createdDateFormatted.indexOf(searchTerm) > -1
            || updatedDateFormatted.indexOf(searchTerm) > -1
            || projectDocs.indexOf(searchTerm) > -1
          )
        })
      }

      return filteredProjects
    },

    /**
     * Format project results into groups for DataTable.
     * Uses groupedProjects and getGroupLabelsArray for proper label transformations.
     * Applies sorting so table view reflects current sort state.
     */
    currentDataGroups() {
      if (this.isGroupingEnabled && Object.keys(this.groupedProjects).length > 0) {
        return Object.entries(this.groupedProjects).map(([groupKey, items]) => ({
          key: groupKey,
          groupName: this.getGroupLabel(groupKey),
          labels: this.getGroupLabelsArray(groupKey),
          isPartOfGroup: true,
          items: this.withUniqueRowKeys(this.applySorting(items), groupKey),
          totalCount: items.length,
        }))
      }

      const items = this.applySorting(this.projectResults || [])
      const groupLabel = this.currentTab?.label || 'Projects'

      return [
        {
          groupName: groupLabel,
          labels: [groupLabel],
          isPartOfGroup: false,
          items: this.withUniqueRowKeys(items, groupLabel),
          totalCount: items.length,
        },
      ]
    },

    allProjects() {
      // Force reactivity when time entries finish loading (triggers re-sort)
      this.projectLoadingCounter // eslint-disable-line no-unused-expressions
      return this.applySorting(this.projectResults)
    },

    actions() {
      const baseActions = [
        {
          event: (project) => this.goToProject(project),
          icon: ['fas', 'arrow-up-right-from-square'],
          name: 'View Profile',
        },
        {
          event: (project) => this.$store.dispatch('internal/onEditProject', project),
          icon: ['fas', 'pen-to-square'],
          name: 'Edit',
        },
        {
          event: (project) => this.togglePublish(project),
          icon: (project) => (project?.isPublished !== false ? ['fas', 'eye-slash'] : ['fas', 'eye']),
          name: (project) => (project?.isPublished !== false ? 'Retract' : 'Publish'),
        },
        {
          event: (project) => this.$store.dispatch('internal/onDeleteProject', project),
          icon: ['fas', 'trash-can'],
          name: 'Delete',
        },
      ]

      if (this.isUserInternalAdmin) {
        baseActions.splice(2, 0, {
          event: (project) => this.openEditCodeDialog(project),
          icon: ['fas', 'tag'],
          name: 'Edit Code',
        })
      }

      return baseActions
    },

    tableHeaders() {
      return [
        {
          text: 'Project Name',
          value: 'projectName',
          sortable: true,
        },
        {
          text: 'Time Consumed',
          value: 'timeConsumed',
          sortable: false,
        },
        {
          text: 'Start Date',
          value: 'startDateFormatted',
          sortable: true,
        },
        {
          text: 'End Date',
          value: 'endDateFormatted',
          sortable: true,
        },
        {
          text: 'Days Left',
          value: 'daysLeft',
          sortable: true,
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
        },
      ]
    },

    /**
     * Available columns for column picker
     */
    availableTableColumns() {
      return this.tableHeaders.map((header) => ({
        text: header.text,
        value: header.value,
        required: header.value === 'projectName' || header.value === 'actions',
      }))
    },

    /**
     * Required columns that cannot be hidden
     */
    requiredColumns() {
      return this.availableTableColumns
        .filter((col) => col.required)
        .map((col) => col.value)
    },

    /**
     * Get visible column values from tableColumns store state
     */
    storeVisibleColumns() {
      if (!this.tableColumns || this.tableColumns.length === 0) {
        // Fallback to default visible columns if store not yet initialized
        return this.visibleColumns
      }
      return this.tableColumns
        .filter((col) => col.visible !== false)
        .map((col) => col.value || col.key)
    },

    projectStatusOptions() {
      // Use Firebase project-status collection data if available, otherwise fallback to defaults
      if (this.projectStatuses && this.projectStatuses.length > 0) {
        return this.projectStatuses
      }

      // Fallback to getting the options from the projects array.
      const statuses = [...new Set(
        this.reactiveProjects
          .map((project) => project.projectStatus)
          .filter((status) => status && status.trim() !== ''),
      )].sort()

      const options = statuses.map((status) => ({
        text: status,
        value: status,
      }))

      // Add "Unknown" option if any projects have no status
      const hasUnknown = this.reactiveProjects.some(
        (project) => !project.projectStatus || project.projectStatus.trim() === '',
      )

      if (hasUnknown) {
        options.unshift({ text: 'Unknown', value: '__unknown__' })
      }

      return options
    },

    projectStatusMap() {
      const map = {}

      this.projectStatuses.forEach((status) => {
        map[status.value] = status.title
      })

      return map
    },

    projectStatusWeightMap() {
      const map = {}

      this.projectStatuses.forEach((status) => {
        const key = status?.value
        const rawWeight = status?.statusWeight ?? status?.weight
        const parsedWeight = Number(rawWeight)
        if (key && Number.isFinite(parsedWeight)) {
          map[key] = parsedWeight
        }
      })

      return map
    },

    childCountByParent() {
      const counts = {}
      const all = this.reactiveProjects || []
      all.forEach((p) => {
        const parentCode = p.parentProjectId
        if (parentCode) {
          counts[parentCode] = (counts[parentCode] || 0) + 1
        }
      })
      return counts
    },

    parentNameByCode() {
      const map = {}
      const all = this.reactiveProjects || []
      all.forEach((p) => {
        if (p.projectCode) {
          map[p.projectCode] = p.projectName || p.projectCode
        }
      })
      return map
    },

    projectTypeOptions() {
      // Use Firebase project-type collection data if available, otherwise fallback to defaults
      if (this.projectTypes && this.projectTypes.length > 0) {
        return this.projectTypes
      }

      // Fallback to getting the options from the projects array.
      const types = [...new Set(
        this.reactiveProjects
          .flatMap((project) => this.getProjectTypeValues(project))
          .filter((type) => type && type.trim() !== ''),
      )].sort()

      return types.map((type) => ({
        text: type,
        value: type,
      }))
    },

    projectPlatformOptions() {
      // Use Firebase project-platform collection data if available
      if (this.projectPlatforms && this.projectPlatforms.length > 0) {
        return this.projectPlatforms.map((p) => ({ text: p.title || p.value, value: p.value }))
      }

      // Fallback: derive unique platforms from loaded projects
      const platforms = [...new Set(
        this.reactiveProjects
          .flatMap((project) => (Array.isArray(project.projectPlatforms) ? project.projectPlatforms : []))
          .filter(Boolean),
      )].sort()

      return platforms.map((p) => ({ text: p, value: p }))
    },

    projectTypeMap() {
      const map = {}

      this.projectTypes.forEach((type) => {
        map[type.value] = type.title
      })

      return map
    },

    groupByOptions() {
      return [
        { text: 'Status', value: 'projectStatus' },
        { text: 'Billing Code', value: 'billingCode' },
        { text: 'Project Type', value: 'projectType' },
        { text: 'Platform', value: 'projectPlatforms' },
        { text: 'Account Manager', value: 'emailAccountManager' },
        { text: 'Project Owner', value: 'projectOwner' },
        { text: 'Project Support', value: 'emailProjectSupporters' },
      ]
    },

    billingCodeOptions() {
      // Combine billing codes from clients and projects for complete coverage
      const clientCodes = Object.keys(this.clientLookup)
      const projectCodes = this.reactiveProjects
        .map((project) => (project.billingCode || '').substring(0, 8))
        .filter((code) => code && code.trim() !== '')

      const billingCodes = [...new Set([...clientCodes, ...projectCodes])].sort()

      // Count projects per billing code in the current tab
      const countMap = {}
      const currentTabProjects = this.tabProjects || []
      currentTabProjects.forEach((project) => {
        const code = (project.billingCode || '').substring(0, 8)
        if (code) {
          countMap[code] = (countMap[code] || 0) + 1
        }
      })

      return billingCodes.map((code) => {
        const clientName = this.clientLookup[code]
        const count = countMap[code] || 0
        const label = clientName ? `${code} — ${clientName}` : code
        return {
          text: count > 0 ? `${label} (${count})` : label,
          value: code,
          count,
          disabled: count === 0,
        }
      })
    },

    /**
     * Filter field configuration for QuickFilterPanel
     */
    projectFilterFields() {
      return [
        {
          key: 'billingCode',
          label: 'Billing Code',
          icon: ['fas', 'code'],
          items: this.billingCodeOptions,
          multiple: true,
        },
        {
          key: 'projectStatus',
          label: 'Status',
          icon: ['fas', 'clipboard-list'],
          items: this.projectStatusOptions,
          multiple: true,
        },
        {
          key: 'projectType',
          label: 'Project Type',
          icon: ['fas', 'layer-group'],
          items: this.projectTypeOptions,
          multiple: true,
        },
        {
          key: 'projectPlatforms',
          label: 'Platform',
          icon: ['fas', 'display'],
          items: this.projectPlatformOptions,
          multiple: true,
        },
        {
          key: 'hasDocs',
          label: 'Has Docs',
          icon: ['fas', 'file-lines'],
          items: [
            { text: 'Yes', value: true },
            { text: 'No', value: false },
          ],
          multiple: false,
        },
        {
          key: 'hasTimeEntries',
          label: 'Has Time Entries',
          icon: ['fas', 'hourglass-end'],
          items: [
            { text: 'Yes', value: true },
            { text: 'No', value: false },
          ],
          multiple: false,
        },
      ]
    },

    /**
     * Current filter values for FilterPanel
     */
    projectFilterValues() {
      return {
        billingCode: this.localFilterData.billingCode,
        projectStatus: this.localFilterData.projectStatus,
        projectType: this.localFilterData.projectType,
        projectPlatforms: this.localFilterData.projectPlatforms,
        hasDocs: this.localFilterData.hasDocs,
        hasTimeEntries: this.localFilterData.hasTimeEntries,
      }
    },

    /**
     * Check if any filters are active (supports both single values and arrays)
     */
    hasActiveFilters() {
      const hasValue = (val) => {
        if (Array.isArray(val)) return val.length > 0
        return val !== null && val !== undefined
      }
      return hasValue(this.localFilterData.billingCode)
        || hasValue(this.localFilterData.projectStatus)
        || hasValue(this.localFilterData.projectType)
        || hasValue(this.localFilterData.projectPlatforms)
        || hasValue(this.localFilterData.hasDocs)
        || hasValue(this.localFilterData.hasTimeEntries)
    },

    /**
     * Count active filters (supports both single values and arrays)
     */
    activeFilterCount() {
      const countValue = (val) => {
        if (Array.isArray(val)) return val.length
        return (val !== null && val !== undefined) ? 1 : 0
      }
      return countValue(this.localFilterData.billingCode)
        + countValue(this.localFilterData.projectStatus)
        + countValue(this.localFilterData.projectType)
        + countValue(this.localFilterData.hasDocs)
        + countValue(this.localFilterData.hasTimeEntries)
    },

    isGroupingEnabled() {
      return this.groupByProperties && this.groupByProperties.length > 0
    },

    groupedProjects() {
      // Force reactivity when time entries finish loading
      this.projectLoadingCounter // eslint-disable-line no-unused-expressions

      if (!this.isGroupingEnabled) {
        return {}
      }

      const groups = {}

      this.projectResults.forEach((project) => {
        // Build composite key from all grouping properties
        const groupValues = this.groupByProperties.map((prop) => {
          if (prop === 'projectType') {
            return this.getProjectTypeDisplay(project) || 'Unassigned'
          }

          return project[prop] || 'Unassigned'
        })
        const groupKey = groupValues.join(':::')

        if (!groups[groupKey]) {
          groups[groupKey] = {
            items: [],
            values: groupValues,
          }
        }
        groups[groupKey].items.push(project)
      })

      // Sort groups alphabetically and flatten to simple object
      const sortedGroups = {}

      this.sortGroupKeys(Object.keys(groups), groups).forEach((key) => {
        // Apply item-level sorting within each group
        sortedGroups[key] = this.applySorting(groups[key].items)
      })

      return sortedGroups
    },

    /**
     * Store the raw group values for each group key for label lookup
     */
    groupValuesMap() {
      if (!this.isGroupingEnabled) {
        return {}
      }

      const map = {}

      this.projectResults.forEach((project) => {
        const groupValues = this.groupByProperties.map((prop) => {
          if (prop === 'projectType') {
            return this.getProjectTypeDisplay(project) || 'Unassigned'
          }

          return project[prop] || 'Unassigned'
        })
        const groupKey = groupValues.join(':::')

        if (!map[groupKey]) {
          map[groupKey] = groupValues
        }
      })

      return map
    },

    /**
     * Show export bar when there's data to export
     */
    showExportBar() {
      return this.projectResults && this.projectResults.length > 0
    },

    /**
     * Export options for the dropdown menu
     */
    exportOptions() {
      const { showExportBar } = this

      return [
        {
          active: showExportBar,
          key: 'csv',
          text: 'CSV',
          callback: this.exportAsCsv,
        },
        {
          active: showExportBar,
          key: 'xlsx',
          text: 'XLSX',
          callback: this.exportAsXlsx,
        },
      ]
    },

    /**
     * Export columns configuration for projects
     * Defines which columns to export and how to format them
     */
    exportColumns() {
      return [
        {
          key: 'projectName',
          columnName: 'Project Name',
          formatAs: 'text',
          columnValue: (item) => item.projectName || '',
        },
        {
          key: 'projectCode',
          columnName: 'Code',
          formatAs: 'text',
          columnValue: (item) => item.projectCode || '',
        },
        {
          key: 'billingCode',
          columnName: 'Billing Code',
          formatAs: 'text',
          columnValue: (item) => item.billingCode || '',
        },
        {
          key: 'projectType',
          columnName: 'Type',
          formatAs: 'text',
          columnValue: (item) => this.getProjectTypeDisplay(item),
        },
        {
          key: 'docs',
          columnName: 'Docs',
          formatAs: 'text',
          columnValue: (item) => this.normalizedProjectDocs(item)
            .map((doc) => doc.title || doc.url)
            .join(', '),
        },
        {
          key: 'projectStatus',
          columnName: 'Status',
          formatAs: 'text',
          columnValue: (item) => item.projectStatus || '',
        },
        {
          key: 'projectDescription',
          columnName: 'Description',
          formatAs: 'text',
          columnValue: (item) => item.projectDescription || '',
        },
        {
          key: 'startDateFormatted',
          columnName: 'Start Date',
          formatAs: 'text',
          columnValue: (item) => item.startDateFormatted || '',
        },
        {
          key: 'endDateFormatted',
          columnName: 'End Date',
          formatAs: 'text',
          columnValue: (item) => item.endDateFormatted || '',
        },
        {
          key: 'daysLeft',
          columnName: 'Days Left',
          formatAs: 'number',
          columnValue: (item) => item.daysLeft || 0,
        },
        {
          key: 'estimatedHours',
          columnName: 'Estimated Hours',
          formatAs: 'number',
          columnValue: (item) => item.estimatedHours || 0,
        },
        {
          key: 'actualHours',
          columnName: 'Actual Hours',
          formatAs: 'number',
          columnValue: (item) => item.actualHours || 0,
        },
        {
          key: 'createdDateFormatted',
          columnName: 'Created Date',
          formatAs: 'text',
          columnValue: (item) => item.createdDateFormatted || '',
        },
        {
          key: 'updatedDateFormatted',
          columnName: 'Updated Date',
          formatAs: 'text',
          columnValue: (item) => item.updatedDateFormatted || '',
        },
      ]
    },

    /**
     * Export groups formatted for the tools/exportAs action
     * Groups the filtered projects based on current grouping or returns single ungrouped list
     */
    exportGroups() {
      if (this.isGroupingEnabled) {
        // Return grouped structure with labels array for ToolsDrawer Jump tab
        return Object.entries(this.groupedProjects).map(([groupKey, items]) => {
          const label = this.getGroupLabel(groupKey)
          const labels = this.getGroupLabelsArray(groupKey)
          return {
            key: groupKey,
            name: label,
            groupName: label,
            labels,
            items,
            rows: items,
          }
        })
      }

      // Default: single ungrouped list
      return [
        {
          key: 'all',
          name: 'All Projects',
          groupName: 'All Projects',
          labels: ['All Projects'],
          items: this.projectResults,
          rows: this.projectResults,
        },
      ]
    },

    /**
     * Export report name using current tab and date
     */
    exportReportName() {
      const tabLabel = this.currentTab?.label || 'Projects'
      const todayFormatted = dates.getFormattedDate(undefined, 'YYYY-MM-DD')

      return `projects_${tabLabel.replace(/\s+/g, '_').toLowerCase()}_${todayFormatted}`
    },

  },

  watch: {
    activeTab() {
      // Update tab counts when switching tabs
      this.updateTabCounts()
    },

    '$route.query.view': function handleViewQueryChange(newView) {
      const normalizedView = newView === 'table' ? 'table' : 'cards'
      if (this.viewMode !== normalizedView) {
        this.viewMode = normalizedView
      }
    },

    '$route.query.groupByProperty': {
      immediate: true,
      handler(newGroupByQuery) {
        const parsedFromQuery = this.parseGroupByPropertiesQuery(newGroupByQuery)
        if (this.areArraysEqual(parsedFromQuery, this.groupByProperties)) {
          return
        }

        const reportKey = 'projects'
        this.groupByProperties = [...parsedFromQuery]
        this.$store.commit('tools/grouping', { groupByProperties: parsedFromQuery, reportKey })
      },
    },

    /**
     * Sync viewMode to URL query parameter.
     * Allows sharing direct links to card or table view.
     */
    viewMode(newMode) {
      const validModes = ['cards', 'table']
      if (!validModes.includes(newMode)) return

      const currentQuery = { ...this.$route.query }
      if (currentQuery.view === newMode) return

      // Omit from URL when default value
      if (newMode === 'cards') {
        delete currentQuery.view
      } else {
        currentQuery.view = newMode
      }

      this.$router.push({ query: currentQuery }).catch((err) => {
        if (err.name !== 'NavigationDuplicated') throw err
      })
    },

    /**
     * Watch for modal close to refresh projects list.
     * When a project is created/updated via the modal, we need to refresh
     * the projects list to reflect the changes in tab counts and cards.
     */
    isProjectModalVisible(isVisible, wasVisible) {
      // Refresh only when modal closes after a successful save/delete
      if (wasVisible && !isVisible && this.projectModified) {
        this.refreshProjects()
        this.projectModified = false
      }
    },

    reactiveProjects: {
      deep: true,
      handler() {
        this.updateTabCounts()
      },
    },

    currentGroupByProperties: {
      immediate: true,
      handler(newGrouping) {
        this.groupByProperties = Array.isArray(newGrouping) ? [...newGrouping] : []
        this.initializeExpandedGroups()
      },
    },

    groupByProperties() {
      this.initializeExpandedGroups()
    },
    currentProject: {
      deep: true,
      immediate: true,
      handler() {
        // if (newProject && newProject.projectId) {
        //   this.currentTimeEntries = this.currentTimeEntries[newProject.projectId] || []
        // } else {
        //   this.currentTimeEntries = []
        // }
      },
    },
    // Watch only the store projects, not the computed $projects that includes projectsById
    projects: {
      deep: true,
      async handler(newProjects, oldProjects) {
        // Prevent circular dependency when we're already loading time entries
        if (this.isLoadingProjectTimeEntries) {
          return false
        }

        const newProjectsJson = JSON.stringify(newProjects)
        const oldProjectsJson = JSON.stringify(oldProjects)

        if (newProjectsJson === oldProjectsJson) {
          return false // No changes detected
        }

        // Only process if projects are loaded.
        if (newProjects && newProjects.length > 0) {
          this.setView(this.query)
        }

        return true
      },
    },
    allProjects: {
      deep: true,
      immediate: true,
      handler() {
        // Do nothing. This is just to trigger reactivity.
      },
    },
    groupedProjects: {
      deep: true,
      immediate: true,
      handler() {
        // Do nothing. This is just to trigger reactivity.
      },
    },
  },

  async created() {
    // Fetch project statuses and types from Firebase when component is created
    try {
      // CRITICAL: Wait for SearchBar's configureStore to complete before accessing modules
      // SearchBar calls configureStore asynchronously which registers the internal module
      // Attempting to register here causes duplicate namespace errors
      const maxRetries = 50 // 5 seconds max wait
      let retries = 0
      let isRegistered = await this.$store.dispatch('isStoreRegistered', 'internal', { root: true })

      while (!isRegistered && retries < maxRetries) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => { setTimeout(resolve, 100) })
        retries += 1
        // eslint-disable-next-line no-await-in-loop
        isRegistered = await this.$store.dispatch('isStoreRegistered', 'internal', { root: true })
      }

      if (!isRegistered) {
        this.$store.commit('tools/snackbar', true)
        this.$store.commit('tools/snack', {
          message: 'Projects initialization timed out. Refresh and try again.',
          status: 'warning',
        })
        return
      }

      // Load both project statuses and types in parallel to optimize loading time.
      await Promise.all([
        this.$store.dispatch('tools/fetchProjectStatuses'),
        this.$store.dispatch('tools/fetchProjectTypes'),
      ])
    } catch (error) {
      // Silently fail - component will use fallback options
    }

    // Initialize tools store options for ToolsDrawer
    this.initializeToolsStoreOptions()

    // Initialize tab counts
    this.updateTabCounts()

    // Initialize viewMode from URL query parameter
    const viewParam = this.$route.query.view
    if (viewParam === 'table') {
      this.viewMode = 'table'
    }

    // Fetch clients in background for card display
    this.fetchClients()

    // Set up real-time Firestore listener for project changes
    this.setupRealtimeListener()
  },

  beforeDestroy() {
    this.teardownRealtimeListener()
  },

  methods: {
    teardownRealtimeListener() {
      unregisterListener(PROJECT_FIRESTORE_LISTENER_KEY)
      this.firestoreListenerUnsubscribe = null
    },

    /**
     * Build deterministic row keys for DataTable to avoid duplicate key warnings
     * when upstream data contains repeated projectId values.
     */
    withUniqueRowKeys(items, scope = 'all') {
      const keyCounts = {}

      return (items || []).map((item, index) => {
        const baseKey = String(
          item.projectId
          || item.project_id
          || item.docId
          || item.projectCode
          || item.billingCode
          || item.projectName
          || `row-${index}`,
        )

        const scopedBaseKey = `${scope}::${baseKey}`
        const occurrence = keyCounts[scopedBaseKey] || 0
        keyCounts[scopedBaseKey] = occurrence + 1

        const uniqueKey = occurrence === 0
          ? scopedBaseKey
          : `${scopedBaseKey}::${occurrence}`

        return {
          ...item,
          __rowKey: uniqueKey,
        }
      })
    },

    async fetchClients() {
      this.loadingClients = true

      try {
        const response = await api.getClients()
        const clients = response?.content || []
        const lookup = {}

        clients.forEach((client) => {
          if (client.billingCode) {
            lookup[client.billingCode] = client.clientName || client.billingCode
          }
        })
        this.clientLookup = lookup
      } catch {
        this.clientLookup = {}
      } finally {
        this.loadingClients = false
      }
    },

    getProjectStatusWeight(statusValue) {
      if (!statusValue) return null
      const weight = this.projectStatusWeightMap[statusValue]
      return Number.isFinite(weight) ? weight : null
    },

    sortGroupKeys(groupKeys, groups) {
      if (!Array.isArray(groupKeys)) return []

      // When grouping by project status, order by configured status weight first.
      if (Array.isArray(this.groupByProperties) && this.groupByProperties[0] === 'projectStatus') {
        return [...groupKeys].sort((leftKey, rightKey) => {
          const leftStatus = groups[leftKey]?.values?.[0] || leftKey
          const rightStatus = groups[rightKey]?.values?.[0] || rightKey
          const leftWeight = this.getProjectStatusWeight(leftStatus)
          const rightWeight = this.getProjectStatusWeight(rightStatus)

          const resolvedLeft = leftWeight ?? Number.NEGATIVE_INFINITY
          const resolvedRight = rightWeight ?? Number.NEGATIVE_INFINITY

          if (resolvedLeft !== resolvedRight) {
            return resolvedRight - resolvedLeft
          }

          return String(leftKey).localeCompare(String(rightKey))
        })
      }

      return [...groupKeys].sort((leftKey, rightKey) => String(leftKey).localeCompare(String(rightKey)))
    },

    /**
     * Apply sorting to an array of items based on current sortByKeys and sortByDirections.
     * Supports multi-level sorting (primary, secondary, tertiary).
     * @param {Array} items - Items to sort
     * @returns {Array} - Sorted items
     */
    applySorting(items) {
      if (!items || items.length === 0) {
        return items
      }

      const { sortByKeys, sortByDirections } = this
      const normalizedSortKeys = (sortByKeys || [])
        .map((sortKey) => {
          if (typeof sortKey === 'string') return sortKey
          return sortKey?.key || sortKey?.column || sortKey?.value || null
        })
        .filter(Boolean)

      // If no sorting configured, return items unchanged
      if (normalizedSortKeys.length === 0) {
        return items
      }

      const normalizedDirections = normalizedSortKeys.map((_, index) => {
        const directionRaw = sortByDirections?.[index] ?? sortByKeys?.[index]?.direction
        const directionText = String(directionRaw || '').toLowerCase()
        return directionRaw === true
          || directionText === 'desc'
          || directionText === 'dsc'
          || directionText === 'descending'
      })

      // Create a copy to avoid mutating original array
      const sortedItems = [...items]

      // Sort using multi-level comparison
      sortedItems.sort((a, b) => {
        let result = 0
        let i = 0

        while (result === 0 && i < normalizedSortKeys.length) {
          const key = normalizedSortKeys[i]
          const isDescending = normalizedDirections[i]

          // Get values for comparison
          let valueA = a[key]
          let valueB = b[key]

          if (key === 'projectType') {
            valueA = this.getProjectTypeDisplay(a)
            valueB = this.getProjectTypeDisplay(b)
          }

          if (key === 'projectStatus') {
            const weightA = this.getProjectStatusWeight(a.projectStatus)
            const weightB = this.getProjectStatusWeight(b.projectStatus)
            if (weightA !== null || weightB !== null) {
              valueA = weightA ?? Number.NEGATIVE_INFINITY
              valueB = weightB ?? Number.NEGATIVE_INFINITY
            }
          }

          if (valueA === undefined && a.normalized) {
            valueA = a.normalized[key]
          }
          if (valueB === undefined && b.normalized) {
            valueB = b.normalized[key]
          }

          // Handle null/undefined values (push to end)
          if (valueA == null && valueB != null) {
            result = 1
          } else if (valueA != null && valueB == null) {
            result = -1
          } else if (valueA != null && valueB != null) {
            // Convert to comparable format
            if (typeof valueA === 'string') valueA = valueA.toLowerCase()
            if (typeof valueB === 'string') valueB = valueB.toLowerCase()

            // Compare values
            if (valueA < valueB) result = -1
            else if (valueA > valueB) result = 1
          }

          // Apply sort direction if there's a difference
          if (result !== 0 && isDescending) {
            result = -result
          }

          i += 1
        }

        return result
      })

      return sortedItems
    },

    /**
     * Initialize grouping and sorting options in tools store.
     * This enables ToolsDrawer to work with Projects view.
     */
    initializeToolsStoreOptions() {
      const reportKey = 'projects' // Must match normalized route key for store getters to work

      // Define grouping options for Projects view
      const groupingOptions = this.groupByOptions

      // Set grouping options in store
      this.$store.commit('tools/groupingOptions', { groupingOptions, reportKey })

      // Initialize empty grouping state if not already set
      if (!this.$store.state.tools.groupByProperties[reportKey]) {
        this.$store.commit('tools/grouping', { groupByProperties: [], reportKey })
      }

      // Use predefined columns from columns.js for consistency
      // These include all properties visible on cards and table
      const projectColumns = columns.projects.map((col) => ({
        ...col,
        text: col.columnName,
        visible: col.visible !== false,
      }))
      const existingColumns = this.$store.state.tools.columns[reportKey]

      if (!columnUtils.hasCompatibleColumnSchema(existingColumns, projectColumns)) {
        const storedPrefs = columnUtils.loadColumnPreferences(reportKey)
        const mergedColumns = columnUtils.applyColumnPreferences(projectColumns, storedPrefs)
        this.$store.commit('tools/columns', { [reportKey]: mergedColumns })
      }

      // Initialize empty sorting state if not already set
      if (!this.$store.state.tools.sortingByKeys || this.$store.state.tools.sortingByKeys.length === 0) {
        this.$store.commit('tools/sortingByKeys', [])
        this.$store.commit('tools/sortingByDirections', [])
      }
    },

    normalizedProjectDocs(project) {
      const docs = Array.isArray(project?.docs) ? project.docs : []
      return docs
        .map((doc) => ({
          title: doc?.title || doc?.name || '',
          url: doc?.url || doc?.href || '',
        }))
        .filter((doc) => doc.title || doc.url)
    },

    getProjectTypeValues(project) {
      if (Array.isArray(project?.projectTypes) && project.projectTypes.length > 0) {
        return project.projectTypes.filter(Boolean)
      }

      return project?.projectType ? [project.projectType] : []
    },

    getProjectTypeLabels(project) {
      const values = this.getProjectTypeValues(project)
      if (values.length === 0) {
        return ['None']
      }

      return values.map((value) => this.projectTypeMap[value] || value)
    },

    getProjectTypeDisplay(project) {
      return this.getProjectTypeLabels(project).join(', ')
    },

    projectMatchesType(project, selectedType) {
      return this.getProjectTypeValues(project).includes(selectedType)
    },

    /**
     * Update tab counts based on current projects
     */
    updateTabCounts() {
      // Safety check to ensure tabs exist
      if (!this.tabs || !Array.isArray(this.tabs)) {
        return
      }

      const allProjects = this.reactiveProjects || []

      this.tabs = this.tabs.map((tab) => {
        let count = 0

        switch (tab.id) {
          case 'active':
            count = allProjects.filter((project) => {
              const { isDeleted, isArchived, isTest, projectStatus } = project
              return !isDeleted && !isArchived && !isTest && (projectStatus === 'in-progress'
              || projectStatus === 'active'
              || projectStatus === 'planning'
              || projectStatus === 'scoping'
              || projectStatus === 'launching')
            }).length
            break

          case 'all':
            count = allProjects.filter((project) => {
              const { isDeleted, isArchived, isTest } = project
              return !isDeleted && !isArchived && !isTest
            }).length
            break

          case 'complete':
            count = allProjects.filter((project) => {
              const { isDeleted, isArchived, isTest, projectStatus } = project
              return !isDeleted && !isArchived && !isTest && (projectStatus === 'complete'
              || projectStatus === 'done')
            }).length
            break

          case 'on-hold':
            count = allProjects.filter((project) => {
              const { isDeleted, isArchived, isTest, projectStatus } = project
              return !isDeleted && !isArchived && !isTest && (projectStatus === 'on-hold' || projectStatus === 'paused')
            }).length
            break

          case 'archived':
            count = allProjects.filter((project) => {
              const { isDeleted, isArchived, isTest } = project
              return !isDeleted && !isTest && isArchived
            }).length
            break

          case 'deleted':
            count = this.deletedProjects.length
            break

          default:
            count = 0
        }

        return { ...tab, count }
      })
    },

    /**
     * Toggle column visibility
     */
    onColumnVisibilityToggle(columnValue) {
      if (this.visibleColumns.includes(columnValue)) {
        this.visibleColumns = this.visibleColumns.filter((col) => col !== columnValue)
      } else {
        this.visibleColumns = [...this.visibleColumns, columnValue]
      }
    },

    /**
     * Reset columns to default visibility
     */
    resetColumns() {
      this.visibleColumns = ['projectName', 'projectCode', 'projectType', 'projectStatus', 'progress', 'timeConsumed', 'startDateFormatted', 'endDateFormatted', 'daysRemaining', 'actions']
    },

    /**
     * Scroll to a specific group in the grouped view
     */
    scrollToGroup(groupKey) {
      // Expand the group if it's collapsed
      if (this.expandedGroups[groupKey] === false) {
        this.$set(this.expandedGroups, groupKey, true)
      }

      this.$nextTick(() => {
        const element = document.getElementById(`group-${groupKey}`)
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      })
    },

    /**
     * Handle grouping changes from ToolsDrawer
     * @param {Array} groupByProperties - Array of property values to group by
     */
    onGroupingChange(groupByProperties) {
      const reportKey = 'projects'
      const normalizedGrouping = this.normalizeGroupByProperties(groupByProperties)
      // Update local state so card view responds to grouping
      this.groupByProperties = normalizedGrouping
      this.syncGroupByPropertiesQuery(normalizedGrouping)
      // Update store for table view
      this.$store.commit('tools/grouping', { groupByProperties: normalizedGrouping, reportKey })
    },

    normalizeGroupByProperties(groupByProperties) {
      const optionOrder = (this.groupByOptions || []).reduce((acc, option, index) => {
        acc[option.value] = index
        return acc
      }, {})
      const validGroupByOptions = new Set(Object.keys(optionOrder))
      const normalized = Array.isArray(groupByProperties) ? groupByProperties : []
      return normalized
        .filter((value, index) => validGroupByOptions.has(value) && normalized.indexOf(value) === index)
        .sort((a, b) => optionOrder[a] - optionOrder[b])
    },

    parseGroupByPropertiesQuery(queryValue) {
      const valuesFromQuery = Array.isArray(queryValue)
        ? queryValue
        : String(queryValue || '').split(',')

      return this.normalizeGroupByProperties(valuesFromQuery.map((value) => String(value || '').trim()).filter(Boolean))
    },

    areArraysEqual(a, b) {
      if (a === b) return true
      if (!Array.isArray(a) || !Array.isArray(b)) return false
      if (a.length !== b.length) return false
      return a.every((value, index) => value === b[index])
    },

    syncGroupByPropertiesQuery(groupByProperties) {
      const currentQuery = { ...this.$route.query }
      const normalizedGrouping = this.normalizeGroupByProperties(groupByProperties)
      const serializedGrouping = normalizedGrouping.join(',')
      const currentSerialized = String(currentQuery.groupByProperty || '')

      if (serializedGrouping) {
        currentQuery.groupByProperty = serializedGrouping
      } else {
        delete currentQuery.groupByProperty
      }

      if (currentSerialized === serializedGrouping) {
        return
      }

      this.$router.push({ query: currentQuery }).catch((err) => {
        if (err.name !== 'NavigationDuplicated') throw err
      })
    },

    /**
     * Handle sorting changes from ToolsDrawer
     * Note: ToolsDrawer already commits to tools/sortingByKeys and sortingByDirections.
     * This handler exists for any additional local state updates if needed.
     * The component's applySorting() method will react to those store changes.
     * @param {Array} sorts - Array of sort configurations with column and direction
     */
    onSortingChange(sorts) {
      // ToolsDrawer already commits sorting to the store
      // This handler is available for any additional local logic if needed
      this.$log.info('[Projects] Sorting changed:', sorts)
    },

    /**
     * Show create project form
     */
    async showFormProject() {
      const action = 'create'
      const formData = makeProject({})

      const result = await this.$store.dispatch('internal/showFormProject', { action, formData })

      this.$log.info('Show Project Form', result)

      return result
    },

    closeTimeEntryView() {
      this.showTimeEntryView = false
    },

    goToProject(project) {
      this.$store.dispatch('internal/goToProject', project)
    },

    onAssignClient(project) {
      this.$store.dispatch('internal/onAssignClient', project)
    },

    sortTimeEntriesByDateAsc(a, b) {
      return new Date(a.timeEntryDate) - new Date(b.timeEntryDate)
    },
    sortTimeEntriesByDateDsc(a, b) {
      return new Date(b.timeEntryDate) - new Date(a.timeEntryDate)
    },
    sortTimeEntriesByAsc(key) {
      if (key === 'timeEntryDate') {
        return this.sortTimeEntriesByDateAsc
      }

      return (a, b) => {
        if (a[key] < b[key]) {
          return -1
        }

        if (a[key] > b[key]) {
          return 1
        }

        return 0
      }
    },
    sortTimeEntriesByDsc(key) {
      if (key === 'timeEntryDate') {
        return this.sortTimeEntriesByDateDsc
      }

      return (a, b) => {
        if (a[key] > b[key]) {
          return -1
        }

        if (a[key] < b[key]) {
          return 1
        }

        return 0
      }
    },

    sortTimeEntries({ direction, key }) {
      return (a, b) => {
        if (direction === 'ASC') {
          return this.sortTimeEntriesByAsc(key)(a, b)
        }

        return this.sortTimeEntriesByDsc(key)(a, b)
      }
    },

    toggleAllProjects() {
      this.showAllProjects = !this.showAllProjects
    },

    toggleGroup(groupName) {
      this.$set(this.expandedGroups, groupName, !this.expandedGroups[groupName])
    },

    /**
     * Get display label for a group based on current grouping properties
     * @param {String} groupKey - The composite key used for grouping (values joined by :::)
     * @returns {String} The display label(s) joined by " > " or the original value as fallback
     */
    getGroupLabel(groupKey) {
      if (!groupKey) {
        return 'Unassigned'
      }

      // Get the raw values array from the map
      const groupValues = this.groupValuesMap[groupKey]

      if (!groupValues || !Array.isArray(groupValues)) {
        return groupKey
      }

      // Map each value to its display label
      const labels = groupValues.map((value, index) => {
        const property = this.groupByProperties[index]

        switch (property) {
          case 'projectType':
            return value
          case 'projectStatus':
            if (value === 'Unassigned') return 'Unknown'
            return this.projectStatusMap[value] || value
          default:
            return value
        }
      })

      return labels.join(' : ')
    },

    /**
     * Get display labels as an array for multi-level grouping
     * Used by ToolsDrawer Jump tab to display hierarchical labels
     * @param {String} groupKey - The composite key used for grouping
     * @returns {Array} Array of display labels for each grouping level
     */
    getGroupLabelsArray(groupKey) {
      if (!groupKey) {
        return ['Unassigned']
      }

      // Get the raw values array from the map
      const groupValues = this.groupValuesMap[groupKey]

      if (!groupValues || !Array.isArray(groupValues)) {
        return [groupKey]
      }

      // Map each value to its display label
      return groupValues.map((value, index) => {
        const property = this.groupByProperties[index]

        switch (property) {
          case 'projectType':
            return value
          case 'projectStatus':
            if (value === 'Unassigned') return 'Unknown'
            return this.projectStatusMap[value] || value
          default:
            return value
        }
      })
    },

    initializeExpandedGroups() {
      // Initialize all groups as expanded when grouping changes
      if (this.isGroupingEnabled) {
        const newExpandedGroups = {}
        Object.keys(this.groupedProjects).forEach((groupName) => {
          newExpandedGroups[groupName] = true
        })
        this.expandedGroups = newExpandedGroups
      }
    },

    handleClearFilters() {
      this.localFilterData = {
        billingCode: null,
        projectStatus: null,
        projectType: null,
        projectPlatforms: null,
        hasDocs: null,
        hasTimeEntries: null,
      }
    },

    /**
     * Handle Reset Filters event from ToolsToolbar
     * Clears all filter values in the local filter data
     */
    onResetFilters() {
      this.handleClearFilters()
    },

    matchesParentChildFilter(project, mode) {
      const isChild = !!project.parentProjectId
      const childCount = this.childCountByParent[project.projectCode] || 0
      const isParent = !isChild && childCount > 0
      const isUnlinked = !isChild && childCount === 0

      switch (mode) {
        case 'parents':
          return isParent
        case 'children':
          return isChild
        case 'unlinked':
          return isUnlinked
        case 'all':
        default:
          return true
      }
    },

    /**
     * @method showTimeEntries
     * @desc Set the view's data before revealing the view to the user.
     * @param {Object} project
     * @param {Promise} project.timeEntries
     * @return {void}
     */
    async showTimeEntries(project) {
      // Set the selected project on the view.
      this.currentProject = project

      // Indicate loading state on the project.
      project.isLoadingTimeEntries = true

      // Indicate loading state on the view.
      this.isLoadingTimeEntries = true

      // Reveal the view.
      this.showTimeEntryView = true

      // Get the project's time entries.
      const projectTimeEntries = project.timeEntries || []

      /**
       * Sort the view's time entries by date.
       * Set the sorted time entries on the view.
       *
       * Apply the sorting configurations in reverse order
       * to ensure the user's latest sort order overrides
       * all previous sort orders.
       */

      // eslint-disable-next-line arrow-body-style
      this.currentTimeEntries = this.timeSortByKeys.reduceRight((sortedArray, sortingConfig) => {
        /**
         * @const {function} sortByKeyAndDirection
         * @desc A function configured with a key and direction to sort by.
         */
        const sortByKeyAndDirection = this.sortTimeEntries(sortingConfig)

        // Return the sorted array.
        return sortedArray.sort(sortByKeyAndDirection)
      }, projectTimeEntries)

      const currentTimeEntriesForProject = this.currentTimeEntries || []

      // Set the view's calculations.

      // Calculate total minutes.
      this.totalMinutes = currentTimeEntriesForProject.reduce((sum, entry) => sum + entry.actionDuration, 0)

      // Format total minutes.
      this.totalMinutesFormatted = format.number(this.totalMinutes)

      this.totalHoursFormatted = format.hhmm(this.totalMinutes)

      // Calculate total billable minutes.
      this.totalBillableMinutes = currentTimeEntriesForProject.reduce((sum, entry) => sum + entry.billableActionDuration, 0)

      // Format total billable minutes.
      this.totalBillableMinutesFormatted = format.number(this.totalBillableMinutes)

      this.totalBillableHoursFormatted = format.hhmm(this.totalBillableMinutes)

      // Calculate total persons who have logged time.
      this.totalPersons = [...new Set(currentTimeEntriesForProject.map((entry) => entry.personName))].length

      // Remove the loading state on the project.
      project.isLoadingTimeEntries = false

      // Remove the loading state on the view.
      this.isLoadingTimeEntries = false
    },

    /**
     * @method hideTimeEntries
     * @desc Hide the view from the user before resetting the view's data.
     * @return {void}
     */
    hideTimeEntries() {
      // Hide the view.
      this.showTimeEntryView = false

      // Reset the view's data.
      this.currentProject = {}
      this.currentTimeEntries = []

      // Reset the view's calculations.
      this.totalMinutes = 0
      this.totalMinutesFormatted = '0'
      this.totalHoursFormatted = '0.00'

      this.totalBillableMinutes = 0
      this.totalBillableMinutesFormatted = '0'
      this.totalBillableHoursFormatted = '0.00'

      this.totalPersons = 0

      // Reset the view's sort direction.
      this.sortDirectionForTimeEntries = 'DSC'

      // Reset the loading state.
      this.isLoadingTimeEntries = false
    },

    exportTimeEntriesAsCsv(project) {
      const {
        currentTimeEntries,
        projectTimeColumns: exportColumns,
        timeSortByKeys: sortByKeys,
      } = this

      const { projectCode } = project

      const todayFormatted = dates.getFormattedDate(undefined, 'YYYY-MM-DD')

      const exportFormat = 'csv'
      const exportName = 'time-entries'
      const exportFileName = `Time Entries for ${projectCode} (Extracted ${todayFormatted})`

      const exportGroups = [
        {
          items: currentTimeEntries,
        },
      ]

      const configExport = {
        exportColumns,
        exportFileName,
        exportFormat,
        exportGroups,
        exportName,
        sortByKeys,
      }

      return this.$store.dispatch('tools/exportAs', configExport)
    },

    /**
     * Export projects as CSV file
     * Uses the tools/exportAs action with CSV format
     */
    exportAsCsv() {
      const {
        exportColumns,
        exportGroups,
        exportReportName: exportName,
      } = this
      const exportFormat = 'csv'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },

    /**
     * Export projects as XLSX file
     * Uses the tools/exportAs action with XLSX format
     */
    exportAsXlsx() {
      const {
        exportColumns,
        exportGroups,
        exportReportName: exportName,
      } = this
      const exportFormat = 'xlsx'

      return this.$store.dispatch('tools/exportAs', { exportColumns, exportFormat, exportGroups, exportName })
    },

    // Bulk Operations
    clearSelection() {
      this.selectedProjects = []
    },

    openBulkEditDialog() {
      this.bulkEditDialog = true
    },

    async bulkArchiveProjects() {
      if (this.selectedProjects.length === 0) return

      try {
        const updatePromises = this.selectedProjects.map((project) => this.$store.dispatch('internal/updateProject', {
          ...project,
          isArchived: true,
        }))

        await Promise.all(updatePromises)

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `${this.selectedProjects.length} projects archived successfully`,
          status: 'success',
        }, { root: true })

        this.clearSelection()
      } catch (error) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Error archiving projects',
          status: 'error',
        }, { root: true })
      }
    },

    closeBulkEditDialog() {
      this.bulkEditDialog = false
      this.bulkEditData = {
        projectStatus: '',
        projectType: '',
        startDate: null,
        endDate: null,
      }
    },

    async applyBulkEdit() {
      if (this.selectedProjects.length === 0) return

      try {
        const updatePromises = this.selectedProjects.map((project) => {
          const updates = { ...project }

          // Only apply non-empty values
          if (this.bulkEditData.projectStatus) {
            updates.projectStatus = this.bulkEditData.projectStatus
          }
          if (this.bulkEditData.projectType) {
            updates.projectType = this.bulkEditData.projectType
          }
          if (this.bulkEditData.startDate) {
            updates.startDate = this.bulkEditData.startDate
          }
          if (this.bulkEditData.endDate) {
            updates.endDate = this.bulkEditData.endDate
          }

          return this.$store.dispatch('internal/updateProject', updates)
        })

        await Promise.all(updatePromises)

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `${this.selectedProjects.length} projects updated successfully`,
          status: 'success',
        }, { root: true })

        this.closeBulkEditDialog()
        this.clearSelection()
      } catch (error) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Error updating projects',
          status: 'error',
        }, { root: true })
      }
    },

    async bulkDeleteProjects() {
      if (this.selectedProjects.length === 0) return

      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(`Are you sure you want to delete ${this.selectedProjects.length} projects?`)
      if (!confirmed) return

      try {
        const deletePromises = this.selectedProjects.map((project) => this.$store.dispatch('internal/deleteProject', project))

        await Promise.all(deletePromises)

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `${this.selectedProjects.length} projects deleted successfully`,
          status: 'success',
        }, { root: true })

        this.clearSelection()
      } catch (error) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Error deleting projects',
          status: 'error',
        }, { root: true })
      }
    },

    findProjectByIdOrCode(identifier) {
      if (!this.projectResults || !Array.isArray(this.projectResults)) {
        return null
      }

      // Try to find by project ID first, then by project code
      return this.projectResults.find((project) => project.projectId === identifier
    || project.project_id === identifier
    || project.projectCode === identifier
    || project.project_code === identifier)
    },

    handleEditProjectQuery(query = {}) {
      const { editProject } = query

      // Don't process if no editProject parameter, no access, modal already visible,
      // or we've already processed this exact query
      if (!editProject
      || !this.hasAccessToProjectManagement
      || this.isProjectModalVisible
      || this.processedEditProjectQuery === editProject) {
        // Return false to indicate we did not process the query.
        return false
      }

      // Find the project by ID or code
      const project = this.findProjectByIdOrCode(editProject)

      if (project) {
        // Mark this query as processed to prevent duplicate processing
        this.processedEditProjectQuery = editProject

        // Use the existing onEditProject action to open the modal
        this.$store.dispatch('internal/onEditProject', project)

        // Clear the query parameter to prevent reopening the modal on navigation
        this.$router.replace({
          path: this.$route.path,
          params: this.$route.params,
          query: { ...this.$route.query, editProject: undefined },
        })
      }

      // Indicate that we processed the query (even if no project found).
      return true
    },

    async setTimeEntriesOnProject(project) {
      let projectWithTimeEntries = null

      try {
        // Fetch and store the time entries for all projects.
        const timeEntriesResponseFromApi = await this.$store.dispatch('tools/fetchTimeEntriesByProjectId', project.projectId)

        // Handle error response from API.
        if (!timeEntriesResponseFromApi) {
          const message = 'No response received when fetching time entries for projects.'

          // Show a message to the user.
          this.$store.commit('tools/snackbar', true, { root: true })
          this.$store.commit('tools/snack', {
            message,
            status: 'error',
          }, { root: true })

          // Exit early with empty time entries.
          return timeEntriesResponseFromApi || { error: true, message }
        }

        // Handle error response from API.
        if (timeEntriesResponseFromApi.error) {
          const message = timeEntriesResponseFromApi.message
            || timeEntriesResponseFromApi.error?.message
            || 'Unknown error occurred when fetching time entries for projects.'

          // Show a message to the user.
          this.$store.commit('tools/snackbar', true, { root: true })
          this.$store.commit('tools/snack', {
            message,
            status: 'error',
          }, { root: true })

          // Exit early with empty time entries.
          return timeEntriesResponseFromApi || { error: true, message }
        }

        const timeEntriesFromApi = timeEntriesResponseFromApi.content || []

        // Ensure timeEntriesFromApi is an array before mapping
        if (!Array.isArray(timeEntriesFromApi)) {
          const message = 'Invalid time entries data received from API.'

          // Show a message to the user.
          this.$store.commit('tools/snackbar', true, { root: true })
          this.$store.commit('tools/snack', {
            message,
            status: 'error',
          }, { root: true })

          return { error: true, message }
        }

        // Transform each time entry, creating an array of promises.
        // eslint-disable-next-line arrow-body-style
        const timeEntryPromises = timeEntriesFromApi.map((timeEntry) => {
          return this.$store.dispatch('tools/transformTimeItem', timeEntry)
        })

        // Wait for all transformations to complete, resulting in the
        // final time entries array.
        const timeEntries = await Promise.all(timeEntryPromises)

        // Update projects with its time entries and calculations.
        projectWithTimeEntries = await this.$store.dispatch('tools/calculateProjectHours', {
          timeEntries,
          project,
        })

        return projectWithTimeEntries
      } catch (error) {
        const message = error?.message || 'Failed to load time entries for project.'

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message,
          status: 'error',
        }, { root: true })

        return { error: true, message }
      } finally {
        const fallbackProject = {
          ...project,
          timeEntries: project.timeEntries || [],
          isLoadingTimeEntries: false,
        }

        const nextProject = projectWithTimeEntries || this.projectsById[project.projectId] || fallbackProject
        nextProject.isLoadingTimeEntries = false

        // Store the project with time entries in a map for quick access.
        this.$set(this.projectsById, project.projectId, nextProject)

        // Increment counter to force reactive updates.
        this.projectLoadingCounter += 1
      }
    },

    async setProjectsWithTimeEntries(projects) {
      // Load time entries for each project and keep per-card loading
      // state in the local map to avoid mutating store project objects.
      const projectList = Array.isArray(projects) ? projects : []

      const hydrateTasks = projectList.map((project) => {
        if (!project?.projectId) {
          return Promise.resolve(null)
        }

        const cachedProject = this.projectsById[project.projectId] || project
        const loadingProject = {
          ...cachedProject,
          isLoadingTimeEntries: true,
        }

        this.$set(this.projectsById, project.projectId, loadingProject)
        this.projectLoadingCounter += 1

        return this.setTimeEntriesOnProject(loadingProject)
      })

      await Promise.allSettled(hydrateTasks)
    },

    async setView(query) {
      // Handle the editProject query parameter if present.
      const isEditing = this.handleEditProjectQuery(query)

      // Fetch and store the time entries for all projects if not editing.
      if (!isEditing) {
        const projects = this.projects || []

        // Set flag to prevent circular dependency
        this.isLoadingProjectTimeEntries = true

        try {
          await this.setProjectsWithTimeEntries(projects)
        } finally {
          this.isLoadingProjectTimeEntries = false
        }
      }

      return true
    },

    /**
     * Toggle the published state of an engagement.
     * Admins can publish/retract any engagement.
     * Creators and project owners can publish their own.
     */
    async togglePublish(project) {
      const email = this.userEmail
      const isAdmin = this.isUserInternalAdmin
      const isOwner = project.createdBy === email
        || project.projectOwner === email
        || project.emailAccountManager === email

      if (!isAdmin && !isOwner) {
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Only the engagement owner or an admin can publish or retract this engagement.',
          status: 'error',
        }, { root: true })
        return
      }

      const next = project.isPublished === false
      const projectCode = project.projectCode || project.project_code
      if (!projectCode) return

      try {
        const payload = {
          isPublished: next,
          updatedAt: new Date().toISOString(),
          updatedBy: email,
        }

        if (next) {
          payload.publishedAt = new Date().toISOString()
          payload.publishedBy = email
        } else {
          payload.publishedAt = null
          payload.publishedBy = null
        }

        await firecollection.set('project', projectCode, payload, { merge: true })

        // Optimistic local update
        const idx = (this.reactiveProjects || []).findIndex(
          (p) => (p.projectCode || p.project_code) === projectCode,
        )
        if (idx !== -1) {
          this.$set(this.reactiveProjects, idx, { ...this.reactiveProjects[idx], ...payload })
        }

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: next ? 'Engagement published.' : 'Engagement retracted — now private.',
          status: 'success',
        }, { root: true })
      } catch (error) {
        this.$log.error('togglePublish failed:', error)
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Failed to update publish state.',
          status: 'error',
        }, { root: true })
      }
    },

    /**
     * Refresh projects data and time entries
     */
    async refreshProjects() {
      if (this.isRefreshing) return

      this.isRefreshing = true

      try {
        // Clear existing time entries cache
        this.projectsById = {}
        this.projectLoadingCounter = 0

        // Invalidate API cache to ensure fresh data
        api.invalidateCache('/timesheet/projects')
        api.invalidateCache('/timesheet/view_time_entries')

        // Refresh project view. The $project watcher will trigger re-fetching of projects' time entries.
        await this.$store.dispatch('internal/setProjectView')
      } catch (error) {
        // Log the error for debugging.
        this.$log.error('Error refreshing projects:', error)

        // Show error message to the user.
        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: 'Failed to refresh projects',
          status: 'error',
        }, { root: true })
      } finally {
        this.isRefreshing = false
      }
    },

    /**
     * Set up a real-time Firestore listener for the project collection.
     * - Modified docs are patched in-place for fast, no-loading-state updates.
     * - Added/removed docs trigger a full refresh.
     * The listener is torn down in beforeDestroy().
     */
    setupRealtimeListener() {
      let isFirstSnapshot = true
      const collectionRef = firecollection.project()
      this.teardownRealtimeListener()

      const unsubscribe = onSnapshot(
        collectionRef,
        (snapshot) => {
          // The first snapshot contains all docs as "added" — skip it since
          // fetchProjects already loaded the initial data.
          if (isFirstSnapshot) {
            isFirstSnapshot = false
            return
          }

          const changes = snapshot.docChanges()
          const needsFullRefresh = changes.some(
            (change) => change.type === 'added' || change.type === 'removed',
          )

          if (needsFullRefresh) {
            this.refreshProjectsFromFirestore()
            return
          }

          // Patch modified documents in-place
          const currentProjects = Array.isArray(this.projects) ? [...this.projects] : []
          let hasChanges = false

          changes.forEach((change) => {
            if (change.type !== 'modified') return

            const firestoreData = { ...change.doc.data(), docId: change.doc.id }
            const projectCode = firestoreData.projectCode || change.doc.id
            const idx = currentProjects.findIndex((p) => p.projectCode === projectCode)

            if (idx !== -1) {
              // Merge Firestore fields into the existing project while preserving
              // API-sourced fields (projectId, projectName, etc.)
              currentProjects[idx] = { ...currentProjects[idx], ...firestoreData }
              hasChanges = true
            }
          })

          if (hasChanges) {
            this.$projects = currentProjects
            this.$store.commit('tools/viewId', 'projects')
            this.updateTabCounts()
          }
        },
        (error) => {
          this.$log.error('[Projects] Firestore listener error:', error)
        },
      )

      this.firestoreListenerUnsubscribe = unsubscribe
      registerListener(PROJECT_FIRESTORE_LISTENER_KEY, () => {
        if (this.firestoreListenerUnsubscribe === unsubscribe) {
          this.firestoreListenerUnsubscribe = null
        }

        unsubscribe()
      })
    },

    /**
     * Refresh Firestore-side project data for added/removed document changes.
     * These changes can invalidate cached project lists, so bypass cache first.
     */
    async refreshProjectsFromFirestore() {
      if (this.isRefreshing) return
      try {
        api.invalidateCache('/timesheet/projects')
        await this.$store.dispatch('internal/setProjectView')
      } catch (error) {
        this.$log.error('[Projects] Error refreshing from Firestore:', error)
      }
    },

    // -------------------------------------------------------------------------
    // Project Code Edit
    // -------------------------------------------------------------------------

    /**
     * Open the edit-code dialog for a project.
     * Only available to internal admin users.
     * @param {Object} project
     */
    openEditCodeDialog(project) {
      this.editCodeProject = project
      this.editCodeNewValue = project.projectCode || ''
      this.editCodeReviewStep = false
      this.editCodeError = ''
      this.editCodeDialog = true
    },

    closeEditCodeDialog() {
      this.editCodeDialog = false
      this.editCodeProject = null
      this.editCodeNewValue = ''
      this.editCodeReviewStep = false
      this.editCodeError = ''
      this.editCodeSaving = false
    },

    /**
     * Validate the new project code value.
     * Returns an error string or empty string if valid.
     */
    validateNewProjectCode(newCode) {
      if (!newCode || !newCode.trim()) return 'Project code is required.'
      const normalized = newCode.trim().toUpperCase()
      if (!/^[A-Z0-9_-]+$/i.test(normalized)) {
        return 'Project code may only contain letters, numbers, hyphens, and underscores.'
      }
      // Check uniqueness against existing projects
      const existing = (this.projects || []).find(
        (p) => (p.projectCode || '').toUpperCase() === normalized
          && (p.projectCode || '').toUpperCase() !== ((this.editCodeProject || {}).projectCode || '').toUpperCase(),
      )
      if (existing) return `Project code "${normalized}" is already in use.`
      return ''
    },

    /**
     * Advance from the input step to the review/confirm step.
     */
    reviewEditCode() {
      const error = this.validateNewProjectCode(this.editCodeNewValue)
      this.editCodeError = error
      if (!error) {
        this.editCodeReviewStep = true
      }
    },

    /**
     * Confirm and save the project code change.
     * Writes to Firestore (soft-delete old doc, create new doc) and
     * calls the API to keep both stores consistent.
     */
    async confirmEditCode() {
      const project = this.editCodeProject
      if (!project) return

      const oldCode = project.projectCode
      const newCode = this.editCodeNewValue.trim().toUpperCase()

      if (oldCode === newCode) {
        this.closeEditCodeDialog()
        return
      }

      this.editCodeSaving = true
      this.editCodeError = ''

      try {
        const userEmail = this.$store.state.auth?.user?.email || 'unknown'
        const updatedAt = new Date().toISOString()

        // 1. Write new Firestore doc
        const newDoc = { ...project }
        newDoc.projectCode = newCode
        newDoc.updatedAt = updatedAt
        newDoc.updatedBy = userEmail
        // Remove transient / UI-only fields before writing to Firestore
        const TRANSIENT_KEYS = ['docId', 'timeEntries', 'isLoadingTimeEntries']
        const filteredEntries = Object.entries(newDoc).filter(
          // eslint-disable-next-line no-underscore-dangle
          ([k]) => !TRANSIENT_KEYS.includes(k) && !k.startsWith('__'),
        )
        const cleanDoc = Object.fromEntries(filteredEntries)

        await firecollection.set('project', newCode, cleanDoc, { merge: false })

        // 2. Soft-delete old Firestore doc
        await firecollection.set('project', oldCode, {
          isDeleted: true,
          isActive: false,
          supersededByProjectCode: newCode,
          supersededAt: updatedAt,
          supersededBy: userEmail,
          updatedAt,
          updatedBy: userEmail,
        }, { merge: true })

        // 3. Update API (best-effort; Firestore is the source of truth here)
        try {
          await api.modifyProject({
            action_type: 'update',
            person_email: userEmail,
            project_code: newCode,
            project_id: project.projectId,
            project_name: project.projectName,
            client_id: project.clientId,
            billing_code: project.billingCode,
          })
          api.invalidateCache('/timesheet/projects')
        } catch (apiError) {
          this.$log.error('[Projects] API project code sync failed:', apiError)
          // Non-fatal: Firestore is updated. Admin can re-sync if needed.
        }

        this.$store.commit('tools/snackbar', true, { root: true })
        this.$store.commit('tools/snack', {
          message: `Project code updated to "${newCode}" successfully.`,
          status: 'success',
        }, { root: true })

        this.closeEditCodeDialog()

        // Trigger a full refresh to reflect the rename
        await this.refreshProjects()
      } catch (error) {
        this.$log.error('[Projects] Error updating project code:', error)
        this.editCodeError = error.message || 'Failed to update project code. Please try again.'
      } finally {
        this.editCodeSaving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.visually-hidden {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
}

// Header styles (matching Time and Organization views)
.projects-view-header-compact {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  .header-toolbar {
    min-height: 56px !important;
    max-height: 56px !important;
    background: white !important;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  }

  .header-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(var(--v-primary-base), 0.08);
  }

  .projects-view-tabs-inline {
    ::v-deep .v-tabs-bar {
      height: 56px !important;
      background-color: white !important;
    }

    ::v-deep .v-tab {
      font-size: 0.875rem;
      font-weight: 500;
      letter-spacing: 0.25px;
      text-transform: none;
      min-width: 120px;
      padding: 0 16px;
      height: 56px;

      &:hover {
        background-color: rgba(var(--v-primary-base), 0.04);
      }
    }

    ::v-deep .v-tab--active {
      color: var(--v-primary-base);
    }

    ::v-deep .v-tabs-slider {
      height: 3px !important;
    }
  }

  .tab-compact {
    padding: 0 16px;
  }

  .add-project-btn-compact {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
  }

  // Tab count chips styling
  .tab-count-chip {
    background-color: #e0e0e0 !important;
    border-color: #bdbdbd !important;
    color: #616161 !important;
    font-weight: 600;
  }
}

// Controls bar styles (matching Time and Organization views)
.controls-bar {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);

  .search-field-compact {
    ::v-deep .v-input__control {
      min-height: 40px;
    }

    ::v-deep .v-input__slot {
      background-color: white !important;
    }

    .search-icon {
      color: rgba(0, 0, 0, 0.54);
    }
  }

  .filter-btn-compact {
    text-transform: none;
    letter-spacing: normal;
    height: 40px;

    &.filter-active {
      background-color: rgba(var(--v-primary-base), 0.08) !important;
      border-color: var(--v-primary-base) !important;
      border-width: 2px !important;
    }
  }

  .group-select-compact {
    ::v-deep .v-input__control {
      min-height: 40px;
    }
  }
}

.project-card-group-row {
  margin-bottom: 2px;
}

.project-card-group-header {
  margin-bottom: 6px;
}

.project-card-group-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(10, 35, 66, 0.08);
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(243, 247, 251, 0.98) 100%);
  box-shadow: 0 10px 24px rgba(10, 35, 66, 0.06);
  color: #17324d;
  text-align: left;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

.project-card-group-toggle:hover {
  border-color: rgba(var(--v-primary-base), 0.2);
  box-shadow: 0 14px 30px rgba(10, 35, 66, 0.09);
  transform: translateY(-1px);
}

.project-card-group-toggle:focus-visible {
  outline: 2px solid rgba(var(--v-primary-base), 0.45);
  outline-offset: 2px;
}

.project-card-group-toggle-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(var(--v-primary-base), 0.1);
  color: var(--v-primary-base);
  flex-shrink: 0;
}

.project-card-group-copy {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-width: 0;
}

.project-card-group-label {
  display: block;
  min-width: 0;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.01em;
  color: #12304b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.project-card-group-meta {
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(18, 48, 75, 0.06);
  color: #4c6278;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

::v-deep .table-group {
  margin-bottom: 2px;
}

::v-deep .table-group + .table-group {
  margin-top: 0;
}

::v-deep .table-group .group-header-row.group-header-sticky {
  align-items: center;
  margin-top: 0 !important;
  margin-bottom: 6px;
  padding: 12px 16px;
  border: 1px solid rgba(10, 35, 66, 0.08);
  border-radius: 14px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(243, 247, 251, 0.98) 100%);
  box-shadow: 0 10px 24px rgba(10, 35, 66, 0.06);
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
}

::v-deep .table-group .group-header-row.group-header-sticky:hover {
  border-color: rgba(var(--v-primary-base), 0.2);
  box-shadow: 0 14px 30px rgba(10, 35, 66, 0.09);
  transform: translateY(-1px);
}

::v-deep .table-group .group-header-row.group-header-sticky:focus-visible {
  outline: 2px solid rgba(var(--v-primary-base), 0.45);
  outline-offset: 2px;
}

::v-deep .table-group .group-header-title-col {
  min-width: 0;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

::v-deep .table-group .group-header-title-col .svg-inline--fa {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-right: 12px !important;
  border-radius: 999px;
  background: rgba(var(--v-primary-base), 0.1);
  color: var(--v-primary-base);
  flex-shrink: 0;
}

::v-deep .table-group .group-header-title-col > span:first-of-type {
  color: #12304b !important;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.01em;
}

::v-deep .table-group .group-header-title-col > span:last-of-type {
  color: #597084;
}

::v-deep .table-group .group-header-count-col {
  justify-content: flex-end;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

::v-deep .table-group .group-header-count-col > div {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(18, 48, 75, 0.06);
  color: #4c6278;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

::v-deep .table-group .group-header-count-col > div + div {
  padding-left: 4px;
}

// Dark mode support
.theme--dark {
  .projects-view-header-compact {
    background: #2d2d2d;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);

    .header-toolbar {
      background: #2d2d2d !important;
      border-bottom-color: rgba(255, 255, 255, 0.12);
    }

    .header-icon-wrapper {
      background: rgba(var(--v-primary-base), 0.15);
    }

    .projects-view-tabs-inline {
      ::v-deep .v-tabs-bar {
        background-color: #2d2d2d !important;
      }
    }

    .tab-count-chip {
      background-color: #424242 !important;
      border-color: #616161 !important;
      color: #e0e0e0 !important;
    }
  }

  .controls-bar {
    background: rgba(255, 255, 255, 0.05);
    border-bottom-color: rgba(255, 255, 255, 0.12);

    .search-field-compact {
      ::v-deep .v-input__slot {
        background-color: rgba(255, 255, 255, 0.05) !important;
      }

      .search-icon {
        color: rgba(255, 255, 255, 0.7);
      }
    }

    .filter-btn-compact.filter-active {
      background-color: rgba(var(--v-primary-base), 0.15) !important;
    }
  }

  .project-card-group-toggle {
    border-color: rgba(255, 255, 255, 0.08);
    background: linear-gradient(180deg, rgba(38, 44, 52, 0.98) 0%, rgba(28, 33, 40, 0.98) 100%);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.24);
    color: #e6edf5;
  }

  .project-card-group-toggle:hover {
    border-color: rgba(var(--v-primary-base), 0.32);
    box-shadow: 0 16px 34px rgba(0, 0, 0, 0.28);
  }

  .project-card-group-toggle-icon {
    background: rgba(var(--v-primary-base), 0.18);
    color: #8cc6ff;
  }

  .project-card-group-label {
    color: #f3f7fb;
  }

  .project-card-group-meta {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(230, 237, 245, 0.82);
  }

  ::v-deep .table-group .group-header-row.group-header-sticky {
    border-color: rgba(255, 255, 255, 0.08);
    background: linear-gradient(180deg, rgba(38, 44, 52, 0.98) 0%, rgba(28, 33, 40, 0.98) 100%);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.24);
  }

  ::v-deep .table-group .group-header-row.group-header-sticky:hover {
    border-color: rgba(var(--v-primary-base), 0.32);
    box-shadow: 0 16px 34px rgba(0, 0, 0, 0.28);
  }

  ::v-deep .table-group .group-header-title-col .svg-inline--fa {
    background: rgba(var(--v-primary-base), 0.18);
    color: #8cc6ff;
  }

  ::v-deep .table-group .group-header-title-col > span:first-of-type {
    color: #f3f7fb !important;
  }

  ::v-deep .table-group .group-header-title-col > span:last-of-type {
    color: rgba(230, 237, 245, 0.72);
  }

  ::v-deep .table-group .group-header-count-col > div {
    background: rgba(255, 255, 255, 0.08);
    color: rgba(230, 237, 245, 0.82);
  }
}

// Projects table cell padding and readability
::v-deep .v-data-table {
  td {
    padding: 8px 16px !important;
  }

  th {
    padding: 8px 16px !important;
  }
}
</style>
